import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { SavedAddress, FreightCarrierType } from "@shared/schema";
import { useCartStore, CartItem } from "@/stores/cart-store";
import { useTruckStore } from "@/stores/truck-store";
import { useAddressStore } from "@/stores/address-store";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { GradeBadge } from "@/components/grade-badge";
import { useGradeBadgeCategoryLookup } from "@/lib/category-meta";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ShoppingCart, Truck, AlertTriangle, CheckCircle2, Package, MapPin, Building2, Trash2, Plus, Minus, Video, ChevronDown, CreditCard, Sparkles, Bug } from "lucide-react";
import { FACTORY_REFURBISHED_LABEL, FACTORY_REFURBISHED_GRADING_ANCHOR } from "@shared/palletDisplay";
import { VideoPlayer } from "@/components/video-player";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatMoney, multiplyMoney, sumMoney, formatPriceForDb, parsePrice } from "@shared/currency";
import CartConflictsModal, { type CartConflict } from "@/components/cart-conflicts-modal";
import { trackBeginCheckout, trackPurchase } from "@/lib/analytics";
import { getShipDateColor } from "@/lib/shipDateColors";
import { normalizeVideoUrl } from "@/lib/utils";
import { useShipDateColorMap } from "@/hooks/useShipDateColorMap";
import { Clock } from "lucide-react";

interface UserAccount {
  id: string;
  companyName: string;
  isActive: boolean;
  isPrimaryContact: boolean;
  primaryContactName: string | null;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
  salesTaxExemptUrl?: string | null;
  minimumOrderQuantity?: number;
}

interface AccountAddress {
  id: string;
  accountId: string;
  isBilling: boolean;
  isShipping: boolean;
  locationName: string;
  line1: string;
  line2: string | null;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  phone: string | null;
  deliveryContactName: string | null;
  deliveryContactPhone: string | null;
  deliveryContactEmail: string | null;
  hasLoadingDock: boolean;
  hasForklift: boolean;
  isDefaultShipping: boolean;
}

const checkoutSchema = z.object({
  customerName: z.string().min(2, "Name must be at least 2 characters"),
  customerEmail: z.string().email("Invalid email address"),
  customerPhone: z.string().min(10, "Phone number must be at least 10 digits"),
  shippingAddress: z.string().min(10, "Address must be at least 10 characters"),
  shippingCity: z.string().min(2, "City is required"),
  shippingState: z.string().min(2, "State is required"),
  shippingZip: z.string().min(5, "ZIP code must be at least 5 digits"),
  hasLoadingDock: z.boolean().default(false),
  hasForklift: z.boolean().default(false),
});

type CheckoutForm = z.infer<typeof checkoutSchema>;

interface UnifiedCarrierQuote {
  carrierId: string | null; // UUID for database carriers, null for API carriers
  carrier: string; // Human-readable carrier name
  carrierType: FreightCarrierType;
  serviceLevel?: string;
  totalCost: number;
  baseFreight: number;
  fuelSurcharge: number;
  fuelSurchargePercent?: number;
  accessorialCharges: Array<{
    code: string;
    name: string;
    amount: number;
  }>;
  totalAccessorials: number;
  distanceSurcharge?: number;
  subtotal: number;
  chargeableWeight?: number;
  dimensionalWeight?: number;
  transitEstimate?: {
    pickupStart?: number;
    pickupEnd?: number;
    deliveryStart?: number;
    deliveryEnd?: number;
    durationDays?: number;
  };
  quoteId?: string;
  expiresAt?: number;
}

export default function Checkout() {
  const [, navigate] = useLocation();
  const { items, poNumber: cartPoNumber, getTotalAmount, clearCart, calculateCapacity, updatePoNumber, removeItem, updateQuantity } = useCartStore();
  const { selectedTruckSize } = useTruckStore();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const userIsInactive = !!user && (user as any).isActive === false;
  // Admin-only freight payload inspector — mirrors server ADMIN_ROLES.
  const isAdminUser = !!(user as any)?.roles?.some((r: string) =>
    ['SOHNEN_ADMIN', 'SHOPJIMMY_ADMIN', 'SUPER_ADMIN'].includes(r)
  );
  const { toast } = useToast();
  const [orderCreated, setOrderCreated] = useState(false);
  const [orderId, setOrderId] = useState("");
  const { selectedAddressId, setSelectedAddressId } = useAddressStore();
  const [freightQuotes, setFreightQuotes] = useState<Map<string, UnifiedCarrierQuote[]>>(new Map());
  const [selectedCarriers, setSelectedCarriers] = useState<Map<string, number>>(new Map());
  const [freightQuotesLoading, setFreightQuotesLoading] = useState(false);
  const [freightQuotesError, setFreightQuotesError] = useState<string | null>(null);
  const [freightQuotesRetryCount, setFreightQuotesRetryCount] = useState(0);
  // Admin-only: captured EFW request/response payload per warehouse shipment.
  const [freightDebug, setFreightDebug] = useState<Map<string, any>>(new Map());
  const [freightDebugOpen, setFreightDebugOpen] = useState(false);
  const [poNumber, setPoNumber] = useState("");
  const [cartConflicts, setCartConflicts] = useState<CartConflict | null>(null);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingQuantity, setEditingQuantity] = useState<number>(0);
  const [pendingDeleteItemId, setPendingDeleteItemId] = useState<string | null>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideoItem, setSelectedVideoItem] = useState<{ serialNumber: string; videoUrl: string; grade: string } | null>(null);
  // Track shipping method per warehouse: 'FREIGHT' (default), 'LOCAL_PICKUP', or 'CUSTOMER_FREIGHT'
  const [appliedStoreCredit, setAppliedStoreCredit] = useState(0);
  // Per-category grade-pill resolver — drives the pallet inventory pill
  // and category-specific tooltip on checkout line items (Task #156).
  const lookupGradeBadgeCategory = useGradeBadgeCategoryLookup();
  const [storeCreditInput, setStoreCreditInput] = useState("");
  const [storeCreditPanelOpen, setStoreCreditPanelOpen] = useState(false);

  const [shippingMethods, setShippingMethods] = useState<Map<string, 'FREIGHT' | 'LOCAL_PICKUP' | 'CUSTOMER_FREIGHT'>>(new Map());
  const [shippingMethodsInitialized, setShippingMethodsInitialized] = useState(false);
  // Confirmation modal state for each shipping method
  const [showPickupConfirmModal, setShowPickupConfirmModal] = useState(false);
  const [pickupConfirmText, setPickupConfirmText] = useState("");
  const [showFreightTermsModal, setShowFreightTermsModal] = useState(false);
  const [freightConfirmText, setFreightConfirmText] = useState("");
  const [showCustomerFreightTermsModal, setShowCustomerFreightTermsModal] = useState(false);
  const [customerFreightConfirmText, setCustomerFreightConfirmText] = useState("");
  const [pendingFormData, setPendingFormData] = useState<CheckoutForm | null>(null);

  const total = getTotalAmount();
  const capacity = calculateCapacity(selectedTruckSize || "unknown");

  // Shared ship-date → color-slot map from /api/inventory/upcoming-ship-dates
  const checkoutShipDateColorMap = useShipDateColorMap();

  // Business day utilities for terms modals
  const addBusinessDays = (startDate: Date, days: number): Date => {
    const result = new Date(startDate);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const dow = result.getDay();
      if (dow !== 0 && dow !== 6) added++;
    }
    return result;
  };
  const formatTermsDate = (date: Date): string =>
    date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  // Parse a YYYY-MM-DD date string as LOCAL midnight (not UTC) to avoid timezone-off-by-one
  const parseLocalDate = (dateStr: string): Date => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  // Latest presale ship date in cart (null when all items are in-stock)
  const latestPresaleDate = useMemo(() => {
    const dates = items
      .filter(i => i.expectedShipDate)
      .map(i => parseLocalDate(i.expectedShipDate!));
    if (dates.length === 0) return null;
    return new Date(Math.max(...dates.map(d => d.getTime())));
  }, [items]);

  // Wire deadline: 1 business day from today
  const wireDeadline = useMemo(() => addBusinessDays(new Date(), 1), []);
  // Pickup/readiness deadline: 5 business days from latest presale date (or today if all in-stock)
  const readinessDeadline = useMemo(() => {
    const base = latestPresaleDate || new Date();
    return addBusinessDays(base, 5);
  }, [latestPresaleDate]);

  // Fetch user account for B2B users
  const { data: userAccount } = useQuery<UserAccount | null>({
    queryKey: ["/api/users/me/account"],
    enabled: isAuthenticated,
  });

  const isB2BUser = !!userAccount;

  // Minimum order quantity enforcement for B2B accounts
  const minOrderQty = isB2BUser ? (userAccount?.minimumOrderQuantity ?? 4) : 0;
  const totalCartQty = items.reduce((sum, item) => sum + item.quantity, 0);
  const minimumOrderMet = !isB2BUser || totalCartQty >= minOrderQty;

  // Fetch local pickup warehouses to show pickup option for eligible warehouses
  // Note: This must be defined BEFORE selectedFreightTotal which depends on it
  interface LocalPickupWarehouse {
    id: string; // UUID for pickupWarehouseId
    warehouseCode: string;
    line1: string;
    line2: string | null;
    city: string;
    region: string;
    postalCode: string;
    localPickupFee: string | null;
  }
  const { data: localPickupWarehouses } = useQuery<LocalPickupWarehouse[]>({
    queryKey: ["/api/shipping/local-pickup-availability"],
    enabled: items.length > 0,
  });

  // Calculate total freight from all selected carriers for tax calculation
  // Also considers local pickup fees when pickup is selected
  // CUSTOMER_FREIGHT has $0 shipping cost (customer arranges their own)
  const getShippingCostForWarehouse = (warehouseCode: string, quotes: UnifiedCarrierQuote[] | null, shipmentLocalPickupInfo: { available: boolean; fee: number } | null): number => {
    const method = shippingMethods.get(warehouseCode) || 'FREIGHT';
    if (method === 'CUSTOMER_FREIGHT') {
      return 0; // Customer arranges their own freight
    }
    if (method === 'LOCAL_PICKUP' && shipmentLocalPickupInfo?.available) {
      return shipmentLocalPickupInfo.fee;
    }
    const selectedIndex = selectedCarriers.get(warehouseCode) ?? 0;
    if (quotes && quotes[selectedIndex]) {
      return quotes[selectedIndex].totalCost;
    }
    return 0;
  };

  const selectedFreightTotal = Array.from(freightQuotes.entries()).reduce((total, [warehouseCode, quotes]) => {
    const selectedIndex = selectedCarriers.get(warehouseCode) ?? 0;
    const method = shippingMethods.get(warehouseCode) || 'FREIGHT';
    if (method === 'CUSTOMER_FREIGHT') {
      return total; // Customer arranges their own freight - $0 cost
    }
    if (method === 'LOCAL_PICKUP') {
      // If local pickup, use the pickup fee from localPickupWarehouses
      const pickupWarehouse = localPickupWarehouses?.find(w => w.warehouseCode === warehouseCode);
      return total + (pickupWarehouse?.localPickupFee ? parseFloat(pickupWarehouse.localPickupFee) : 0);
    }
    if (quotes && quotes[selectedIndex]) {
      return total + quotes[selectedIndex].totalCost;
    }
    return total;
  }, 0);

  // Fetch cart data to get sales tax information and shipping method preference
  // Include freight amount in query to get accurate tax calculation
  interface CartDataResponse {
    salesTax: { taxRate: number; taxAmount: number } | null;
    shippingMethod: 'FREIGHT' | 'LOCAL_PICKUP' | null;
    selectedPickupWarehouseId: string | null;
  }
  const { data: cartData } = useQuery<CartDataResponse>({
    queryKey: selectedFreightTotal > 0 ? [`/api/cart?freightAmount=${selectedFreightTotal}`] : ["/api/cart"],
    enabled: isAuthenticated && items.length > 0,
  });

  const salesTax = cartData?.salesTax;

  // Re-cap applied store credit when totals change (cart edits, shipping selection, tax updates)
  // This prevents stale over-applied credit from triggering a server reject at submit
  useEffect(() => {
    if (appliedStoreCredit <= 0) return;
    const grossOrderTotal = total + selectedFreightTotal + (salesTax?.taxAmount || 0);
    if (grossOrderTotal <= 0) return;
    if (appliedStoreCredit > grossOrderTotal) {
      setAppliedStoreCredit(grossOrderTotal);
      setStoreCreditInput(grossOrderTotal.toFixed(2));
    }
  }, [total, selectedFreightTotal, salesTax]);

  // Initialize shipping methods from cart data (persisted user preference)
  useEffect(() => {
    if (cartData && localPickupWarehouses && !shippingMethodsInitialized) {
      if (cartData.shippingMethod === 'LOCAL_PICKUP' && cartData.selectedPickupWarehouseId) {
        // Find the warehouse code for the selected pickup warehouse
        const pickupWarehouse = localPickupWarehouses.find(w => w.id === cartData.selectedPickupWarehouseId);
        if (pickupWarehouse) {
          setShippingMethods(prev => {
            const updated = new Map(prev);
            updated.set(pickupWarehouse.warehouseCode, 'LOCAL_PICKUP');
            return updated;
          });
        }
      }
      setShippingMethodsInitialized(true);
    }
  }, [cartData, localPickupWarehouses, shippingMethodsInitialized]);

  // Fetch account addresses for B2B users
  const { data: accountAddresses } = useQuery<AccountAddress[]>({
    queryKey: [`/api/accounts/${userAccount?.id}/addresses`],
    enabled: isB2BUser && !!userAccount?.id,
  });

  // Fetch saved addresses for regular users (legacy)
  const { data: savedAddresses } = useQuery<SavedAddress[]>({
    queryKey: ["/api/users/addresses"],
    enabled: isAuthenticated && !isB2BUser,
  });

  // Fetch store credit balance for authenticated users
  const { data: storeCreditBalance } = useQuery<{ availableBalance: number }>({
    queryKey: ["/api/store-credit/balance"],
    enabled: isAuthenticated,
    select: (data: any) => ({ availableBalance: parseFloat(data?.availableBalance ?? "0") || 0 }),
  });

  // Auto-select default shipping address for B2B users
  useEffect(() => {
    if (isB2BUser && accountAddresses && accountAddresses.length > 0) {
      // Check if current selection is valid
      const currentSelectionValid = selectedAddressId && 
        accountAddresses.some(addr => addr.id === selectedAddressId && addr.isShipping);

      // If no valid selection, auto-select default or first shipping address
      if (!currentSelectionValid) {
        const defaultAddress = accountAddresses.find(addr => addr.isDefaultShipping && addr.isShipping);
        const firstShippingAddress = accountAddresses.find(addr => addr.isShipping);
        const addressToSelect = defaultAddress || firstShippingAddress;
        if (addressToSelect) {
          setSelectedAddressId(addressToSelect.id);
        }
      }
    }
  }, [isB2BUser, accountAddresses, selectedAddressId, setSelectedAddressId]);

  // Auto-select default shipping address for regular (non-B2B) users
  useEffect(() => {
    if (!isB2BUser && savedAddresses && savedAddresses.length > 0) {
      // Check if current selection is valid
      const currentSelectionValid = selectedAddressId && 
        (selectedAddressId === "new" || savedAddresses.some(addr => addr.id === selectedAddressId));

      // If no valid selection, auto-select default or first address
      if (!currentSelectionValid) {
        const defaultAddress = savedAddresses.find(addr => addr.isDefault);
        const addressToSelect = defaultAddress || savedAddresses[0];
        if (addressToSelect) {
          setSelectedAddressId(addressToSelect.id);
        }
      }
    }
  }, [isB2BUser, savedAddresses, selectedAddressId, setSelectedAddressId]);

  const form = useForm<CheckoutForm>({
    resolver: zodResolver(checkoutSchema),
    defaultValues: {
      customerName: isAuthenticated && user ? 
        `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "" : "",
      customerEmail: isAuthenticated && user ? user.email || "" : "",
      customerPhone: isAuthenticated && user ? user.phone || "" : "",
      shippingAddress: "",
      shippingCity: "",
      shippingState: "",
      shippingZip: "",
      hasLoadingDock: false,
      hasForklift: false,
    },
  });

  // Watch form fields for freight calculation - must be at component level, not in useEffect
  const watchedShippingState = form.watch("shippingState");
  const watchedShippingCity = form.watch("shippingCity");
  const watchedShippingZip = form.watch("shippingZip");
  const watchedHasLoadingDock = form.watch("hasLoadingDock");
  const watchedHasForklift = form.watch("hasForklift");

  // Auto-populate form when saved address is selected (for non-B2B users)
  useEffect(() => {
    if (!isB2BUser && selectedAddressId && savedAddresses && user) {
      const address = savedAddresses.find(addr => addr.id === selectedAddressId);
      if (address) {
        form.setValue("customerName", `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "");
        form.setValue("customerEmail", user.email || "");
        form.setValue("customerPhone", address.phone || user.phone || "");
        form.setValue("shippingAddress", address.addressLine1 + (address.addressLine2 ? ", " + address.addressLine2 : ""));
        form.setValue("shippingCity", address.city);
        form.setValue("shippingState", address.state);
        form.setValue("shippingZip", address.zipCode);
        form.setValue("hasLoadingDock", address.hasLoadingDock || false);
        form.setValue("hasForklift", address.hasForklift || false);
      }
    }
  }, [isB2BUser, selectedAddressId, savedAddresses, user, form]);

  useEffect(() => {
    document.title = `OpenBox by ShopJimmy`;
  }, []);

  useEffect(() => {
    if (items.length === 0 && !orderCreated) {
      navigate("/");
    }
  }, [items.length, navigate, orderCreated]);

  // Sync local PO number with cart store
  useEffect(() => {
    setPoNumber(cartPoNumber || "");
  }, [cartPoNumber]);

  // Auto-save PO number on blur
  const handlePoNumberBlur = () => {
    if (poNumber !== (cartPoNumber || "")) {
      updatePoNumber(poNumber);
    }
  };

  // Redirect unauthenticated users to login, saving their cart via guest→auth migration on return
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      sessionStorage.setItem('checkout_redirect', '/checkout');
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // AUTO-CHECK for cart conflicts on page load
  useEffect(() => {
    const runConflictCheck = async () => {
      if (items.length === 0 || !isAuthenticated) return;

      try {
        const conflicts = await checkConflicts();
        if (conflicts.removed.length > 0 || conflicts.adjusted.length > 0) {
          setCartConflicts(conflicts);
        }
      } catch (error) {
        console.error("Auto-conflict check failed:", error);
        // Silent failure - user can still checkout and will see conflicts then
      }
    };

    runConflictCheck();
  }, []); // Run once on mount

  // Track begin_checkout event when checkout page loads with items
  // Uses cart signature to track each unique cart state, allowing re-fires when cart changes
  const beginCheckoutTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (items.length > 0) {
      const cartSignature = items.map(i => `${i.id}:${i.quantity}`).join('|');
      if (beginCheckoutTrackedRef.current !== cartSignature) {
        beginCheckoutTrackedRef.current = cartSignature;
        const totalValue = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        trackBeginCheckout(
          items.map(item => ({
            id: item.modelId,
            modelNumber: item.modelName,
            brand: item.brand,
            grade: item.grade,
            price: item.price,
            quantity: item.quantity,
          })),
          totalValue
        );
      }
    }
  }, [items]);

  // Helper function to fetch freight quote with retry logic
  const fetchFreightQuoteWithRetry = async (
    payload: any,
    maxRetries: number = 3
  ): Promise<{ quotes: UnifiedCarrierQuote[]; debug: any }> => {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const data = await apiRequest('POST', '/api/freight/quote-for-cart', payload);
        // Admins get an { quotes, freightDebug } object; everyone else a bare array.
        if (Array.isArray(data)) {
          return { quotes: data, debug: null };
        }
        return { quotes: data?.quotes ?? [], debug: data?.freightDebug ?? null };
      } catch (error: any) {
        lastError = error;
        console.error(`Freight quote attempt ${attempt + 1}/${maxRetries} failed:`, error);
        
        // Don't retry on client errors (4xx) except 429 (rate limit)
        if (error?.status >= 400 && error?.status < 500 && error?.status !== 429) {
          throw error;
        }
        
        // Wait before retrying with exponential backoff (1s, 2s, 4s)
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }
    
    throw lastError || new Error('Failed to get freight quote after multiple attempts');
  };

  // Calculate freight quotes for each warehouse
  useEffect(() => {
    const calculateFreightQuotes = async () => {
      let state: string | undefined;
      let city: string | undefined;
      let zip: string | undefined;
      let hasLoadingDock = false;
      let hasForklift = false;

      if (isB2BUser && selectedAddressId) {
        const address = accountAddresses?.find(addr => addr.id === selectedAddressId);
        if (!address) {
          setFreightQuotes(new Map());
          setFreightDebug(new Map());
          return;
        }
        state = address.region;
        city = address.city;
        zip = address.postalCode;
        hasLoadingDock = address.hasLoadingDock;
        hasForklift = address.hasForklift;
      } else {
        // Use watched values from component level
        state = watchedShippingState;
        city = watchedShippingCity;
        zip = watchedShippingZip;
        hasLoadingDock = watchedHasLoadingDock;
        hasForklift = watchedHasForklift;
      }

      if (!state || state.length !== 2 || !city || !zip) {
        setFreightQuotes(new Map());
        setFreightDebug(new Map());
        return;
      }

      // Group items by warehouse
      const warehouseItemsMap = new Map<string, typeof items>();
      items.forEach(item => {
        const warehouseCode = item.warehouseCode || 'UNKNOWN';
        if (!warehouseItemsMap.has(warehouseCode)) {
          warehouseItemsMap.set(warehouseCode, []);
        }
        warehouseItemsMap.get(warehouseCode)!.push(item);
      });

      setFreightQuotesLoading(true);
      setFreightQuotesError(null);

      try {
        const quotesMap = new Map<string, UnifiedCarrierQuote[]>();
        const debugMap = new Map<string, any>();
        const newSelectedCarriers = new Map<string, number>();
        const warehouseErrors: string[] = [];

        // Calculate freight for each warehouse with retry logic
        await Promise.all(
          Array.from(warehouseItemsMap.entries()).map(async ([warehouseCode, warehouseItems]) => {
            try {
              // Get warehouse address from first item
              const firstItem = warehouseItems[0];
              const warehouseAddress = (firstItem as any).warehouseAddress || '';
              
              // Get destination address based on user type
              let destinationAddress = '';
              if (isB2BUser && selectedAddressId) {
                const selectedAddr = accountAddresses?.find(addr => addr.id === selectedAddressId);
                if (selectedAddr) {
                  destinationAddress = `${selectedAddr.line1}${selectedAddr.line2 ? ', ' + selectedAddr.line2 : ''}`;
                }
              } else {
                // Use form watch for regular users
                const formAddress = form.watch("shippingAddress");
                destinationAddress = formAddress || '';
              }
              
              const { quotes: carrierQuotes, debug: carrierDebug } = await fetchFreightQuoteWithRetry({
                cartItems: warehouseItems.map(item => ({
                  modelId: item.modelId,
                  quantity: item.quantity,
                })),
                originAddress: warehouseAddress,
                destinationState: state!.toUpperCase(),
                destinationCity: city!,
                destinationZip: zip!,
                destinationAddress,
                hasLoadingDock,
                hasForklift,
              });

              quotesMap.set(warehouseCode, carrierQuotes);
              if (carrierDebug) {
                debugMap.set(warehouseCode, carrierDebug);
              }

              // Default to selecting the cheapest carrier (index 0, since quotes are sorted)
              if (carrierQuotes.length > 0 && !selectedCarriers.has(warehouseCode)) {
                newSelectedCarriers.set(warehouseCode, 0);
              }
            } catch (error: any) {
              console.error(`Error calculating freight for ${warehouseCode}:`, error);
              warehouseErrors.push(warehouseCode);
            }
          })
        );

        setFreightQuotes(quotesMap);
        setFreightDebug(debugMap);

        // Update selected carriers with defaults for new warehouses
        setSelectedCarriers(prev => {
          const updated = new Map(prev);
          newSelectedCarriers.forEach((value, key) => {
            if (!updated.has(key)) {
              updated.set(key, value);
            }
          });
          return updated;
        });

        // If all warehouse quotes failed, show error with retry option
        if (warehouseErrors.length > 0 && quotesMap.size === 0) {
          setFreightQuotesError("Unable to calculate freight quotes. Please try again.");
        } else if (warehouseErrors.length > 0) {
          // Some succeeded, some failed - show partial warning
          setFreightQuotesError(`Freight unavailable for some shipments. You may proceed or retry.`);
        }
      } catch (error) {
        console.error("Error calculating freight quotes:", error);
        setFreightQuotesError("Unable to calculate freight quotes. Please try again.");
        setFreightQuotes(new Map());
        setFreightDebug(new Map());
      } finally {
        setFreightQuotesLoading(false);
      }
    };

    calculateFreightQuotes();
  }, [isB2BUser, selectedAddressId, accountAddresses, watchedShippingState, watchedShippingCity, watchedShippingZip, watchedHasLoadingDock, watchedHasForklift, items, freightQuotesRetryCount]);

  // Manual retry handler for freight quotes
  const retryFreightQuotes = () => {
    setFreightQuotesRetryCount(prev => prev + 1);
  };

  // Ref to capture cart items before mutation (totals come from API response for accuracy)
  const purchaseTrackingDataRef = useRef<{ items: typeof items } | null>(null);

  const createOrderMutation = useMutation({
    mutationFn: async (data: CheckoutForm) => {
      // Calculate total shipping cost considering freight, local pickup, and customer freight options
      let totalShippingCost = 0;
      let totalFreightCost = 0;
      let totalPickupFee = 0;
      let primaryShippingMethod: 'FREIGHT' | 'LOCAL_PICKUP' | 'CUSTOMER_FREIGHT' = 'FREIGHT';
      let primaryPickupWarehouseId: string | null = null;
      let primaryPickupFee: number | null = null;
      let selectedCarrierInfo: UnifiedCarrierQuote | null = null;
      
      // Group items by warehouse to determine shipping method per warehouse
      const warehouseCodes = new Set(items.map(item => item.warehouseCode).filter((code): code is string => !!code));
      
      warehouseCodes.forEach((warehouseCode: string) => {
        const method = shippingMethods.get(warehouseCode) || 'FREIGHT';
        
        if (method === 'CUSTOMER_FREIGHT') {
          // Customer arranges their own freight - $0 shipping cost
          // Freight details will be collected on order detail page after order creation
          primaryShippingMethod = 'CUSTOMER_FREIGHT';
          // For customer freight, use the pickup warehouse same as local pickup
          const pickupWarehouse = localPickupWarehouses?.find(w => w.warehouseCode === warehouseCode);
          if (pickupWarehouse) {
            primaryPickupWarehouseId = pickupWarehouse.id;
          }
        } else if (method === 'LOCAL_PICKUP') {
          // Find the pickup warehouse info
          const pickupWarehouse = localPickupWarehouses?.find(w => w.warehouseCode === warehouseCode);
          const pickupFee = pickupWarehouse?.localPickupFee ? parseFloat(pickupWarehouse.localPickupFee) : 0;
          totalPickupFee += pickupFee;
          totalShippingCost += pickupFee;
          
          // Set as primary if this is the first local pickup warehouse
          if (!primaryPickupWarehouseId && pickupWarehouse) {
            primaryShippingMethod = 'LOCAL_PICKUP';
            primaryPickupWarehouseId = pickupWarehouse.id; // Use the actual warehouse UUID
            primaryPickupFee = pickupFee;
          }
        } else {
          // FREIGHT - use freight quotes
          const quotes = freightQuotes.get(warehouseCode);
          const selectedIndex = selectedCarriers.get(warehouseCode) ?? 0;
          if (quotes && quotes[selectedIndex]) {
            const quote = quotes[selectedIndex];
            totalFreightCost += quote.totalCost;
            totalShippingCost += quote.totalCost;
            
            // Use first freight carrier as the primary carrier info
            if (!selectedCarrierInfo) {
              selectedCarrierInfo = quote;
            }
          }
        }
      });

      // Capture cart items for GA4 tracking (totals will come from API response)
      purchaseTrackingDataRef.current = {
        items: [...items], // Copy array to prevent mutation
      };

      const grandTotal = total + totalShippingCost + (salesTax?.taxAmount || 0);

      // Get shipping info based on user type
      let shippingState: string;
      let hasLoadingDock: boolean;
      let hasForklift: boolean;
      let customerInfo: any;

      if (isB2BUser && selectedAddressId) {
        const address = accountAddresses?.find(addr => addr.id === selectedAddressId);
        if (!address) {
          throw new Error("Please select a valid shipping address");
        }
        shippingState = address.region;
        hasLoadingDock = address.hasLoadingDock;
        hasForklift = address.hasForklift;
        // Logistics contact info (delivery contact for this address)
        customerInfo = {
          name: address.deliveryContactName || userAccount?.primaryContactName || user?.email || "",
          email: address.deliveryContactEmail || userAccount?.primaryContactEmail || user?.email || "",
          phone: address.deliveryContactPhone || userAccount?.primaryContactPhone || "",
          address: `${address.line1}${address.line2 ? ', ' + address.line2 : ''}, ${address.city}, ${address.region} ${address.postalCode}`,
          // Purchaser info (the logged-in user who placed the order)
          purchaserName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || "",
          purchaserEmail: user?.email || "",
          companyName: userAccount?.companyName || "",
          // Include full shipping address details for email
          shippingAddress: {
            name: address.deliveryContactName || userAccount?.primaryContactName || "",
            street1: address.line1,
            street2: address.line2 || undefined,
            city: address.city,
            state: address.region,
            zip: address.postalCode,
          },
        };
      } else {
        shippingState = data.shippingState;
        hasLoadingDock = data.hasLoadingDock;
        hasForklift = data.hasForklift;
        customerInfo = {
          name: data.customerName,
          email: data.customerEmail,
          phone: data.customerPhone,
          address: `${data.shippingAddress}, ${data.shippingCity}, ${data.shippingState} ${data.shippingZip}`,
        };
      }

      // Cast values to correct types for orderData
      const finalPickupFee = primaryPickupFee as number | null;
      const finalFreightCost = totalFreightCost as number;
      const carrierInfo = selectedCarrierInfo as UnifiedCarrierQuote | null;
      
      const orderData = {
        userId: isAuthenticated && user ? user.id : null,
        accountId: isB2BUser && userAccount ? userAccount.id : null,
        addressId: isB2BUser && selectedAddressId ? selectedAddressId : null,
        poNumber: isB2BUser ? (poNumber || null) : null, // Include PO number for B2B users
        customerInfo,
        items: items.map(item => ({
          inventoryId: item.inventoryId,
          unitId: item.unitId || null,
          quantity: item.quantity,
          priceAtPurchase: formatPriceForDb(item.price), // Format decimal price for database
          grade: item.grade,
        })),
        truckSize: selectedTruckSize,
        capacityUsedPercentage: capacity.capacityUsedPercentage.toString(),
        totalAmount: grandTotal.toString(),
        // Shipping method - use the primary method determined above
        shippingMethod: primaryShippingMethod,
        pickupWarehouseId: primaryPickupWarehouseId, // For local pickup orders
        pickupFee: finalPickupFee !== null ? finalPickupFee.toString() : null, // For local pickup orders
        // Include selected carrier information (for freight orders)
        freightCarrierId: carrierInfo?.carrierId || null, // UUID or null
        freightCarrierName: carrierInfo?.carrier || null, // Human-readable name
        freightCarrierType: carrierInfo?.carrierType || null, // DATABASE or UBER_FREIGHT
        freightServiceLevel: carrierInfo?.serviceLevel || null,
        freightQuoteId: carrierInfo?.quoteId || null, // External quote ID
        freightBaseAmount: carrierInfo?.baseFreight != null ? carrierInfo.baseFreight.toString() : (finalFreightCost > 0 ? finalFreightCost.toString() : null),
        freightFuelSurcharge: carrierInfo?.fuelSurcharge?.toString() || null,
        freightAccessorials: carrierInfo?.accessorialCharges || null,
        freightTotal: finalFreightCost > 0 ? finalFreightCost.toString() : null,
        shippingState,
        hasLoadingDock,
        hasForklift,
        status: "pending",
        // Customer freight details - null at order creation, filled in on order detail page
        customerFreightCarrierName: null,
        customerFreightPickupDate: null,
        customerFreightPickupTime: null,
        customerFreightContactName: null,
        customerFreightContactPhone: null,
        customerFreightNotes: null,
      };

      return await apiRequest('POST', '/api/orders', {
        ...orderData,
        ...(appliedStoreCredit > 0 ? { storeCreditAmount: appliedStoreCredit.toFixed(2) } : {}),
      });
    },
    onSuccess: (data) => {
      // Track purchase event using captured items from ref and authoritative totals from API response
      const trackingData = purchaseTrackingDataRef.current;
      if (trackingData) {
        // Use parsePrice for robust handling of null, undefined, and formatted strings
        const orderTotal = parsePrice(data.totalAmount);
        const orderTax = parsePrice(data.salesTaxAmount);
        const orderShipping = parsePrice(data.freightTotal);
        const orderPickupFee = parsePrice(data.pickupFee);

        trackPurchase(
          trackingData.items.map(item => ({
            id: item.modelId,
            modelNumber: item.modelName,
            brand: item.brand,
            grade: item.grade,
            price: typeof item.price === 'number' ? item.price : parsePrice(item.price), // Ensure numeric
            quantity: item.quantity,
          })),
          {
            transactionId: data.id,
            value: orderTotal,
            tax: orderTax,
            shipping: orderShipping + orderPickupFee, // Include pickup fee as part of shipping
          }
        );

        // Clear the ref after tracking
        purchaseTrackingDataRef.current = null;
      }

      setOrderCreated(true);
      setOrderId(data.id);
      clearCart();
      
      // Redirect to order details page with wire transfer instructions
      navigate(`/orders/${data.id}`);
    },
    onError: (error: any) => {
      // Extract detailed error message from API response
      const errorDetails = error?.details || error?.message || "Failed to create order. Please try again.";

      toast({
        title: "Order Error",
        description: errorDetails,
        variant: "destructive",
      });
    },
  });

  const handleAddressChange = (value: string) => {
    setSelectedAddressId(value);

    if (value === "" || value === "new") {
      form.setValue("customerName", isAuthenticated && user ? 
        `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "" : "");
      form.setValue("customerEmail", isAuthenticated && user ? user.email || "" : "");
      form.setValue("customerPhone", isAuthenticated && user ? user.phone || "" : "");
      form.setValue("shippingAddress", "");
      form.setValue("shippingCity", "");
      form.setValue("shippingState", "");
      form.setValue("shippingZip", "");
      form.setValue("hasLoadingDock", false);
      form.setValue("hasForklift", false);
    } else {
      const address = savedAddresses?.find(addr => addr.id === value);
      if (address && user) {
        form.setValue("customerName", `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "");
        form.setValue("customerEmail", user.email || "");
        form.setValue("customerPhone", address.phone || user.phone || "");
        form.setValue("shippingAddress", address.addressLine1 + (address.addressLine2 ? ", " + address.addressLine2 : ""));
        form.setValue("shippingCity", address.city);
        form.setValue("shippingState", address.state);
        form.setValue("shippingZip", address.zipCode);
        form.setValue("hasLoadingDock", address.hasLoadingDock || false);
        form.setValue("hasForklift", address.hasForklift || false);
      }
    }
  };

  // Execute non-B2B order after validation and confirmation
  const executeNonB2BSubmit = async (data: CheckoutForm) => {
    // Check for cart conflicts before creating order
    try {
      const conflicts = await checkConflicts();
      if (conflicts.removed.length > 0 || conflicts.adjusted.length > 0) {
        setCartConflicts(conflicts);
        return; // Stop submission, show modal
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to verify cart availability. Please try again.",
        variant: "destructive",
      });
      return;
    }

    // No conflicts, proceed with order creation
    createOrderMutation.mutate(data);
  };

  const onSubmit = async (data: CheckoutForm) => {
    // Require authentication to complete checkout
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please sign in or create an account to complete your order.",
        variant: "destructive",
      });
      // Store return URL and redirect to login
      sessionStorage.setItem('checkout_redirect', '/checkout');
      navigate('/login');
      return;
    }

    if (capacity.capacityUsedPercentage > 100) {
      toast({
        title: "Capacity Exceeded",
        description: "Your cart exceeds truck capacity. Please remove some items.",
        variant: "destructive",
      });
      return;
    }

    // Always show the appropriate terms modal before placing an order
    openTermsModal(data);
  };

  const checkConflicts = async (): Promise<CartConflict> => {
    setIsCheckingConflicts(true);
    try {
      const result = await apiRequest('POST', '/api/cart/conflicts', {});
      return result as CartConflict;
    } finally {
      setIsCheckingConflicts(false);
    }
  };

  const handleConflictsAcknowledged = async () => {
    // Reload cart from server and invalidate cache
    queryClient.invalidateQueries({ queryKey: ['/api/cart'] });
    setCartConflicts(null);
  };

  // Check if any warehouse has local pickup selected
  const hasLocalPickupSelected = (): boolean =>
    Array.from(shippingMethods.values()).some(method => method === 'LOCAL_PICKUP');

  const hasCustomerFreightSelected = (): boolean =>
    Array.from(shippingMethods.values()).some(method => method === 'CUSTOMER_FREIGHT');

  const hasFreightSelected = (): boolean =>
    Array.from(shippingMethods.values()).some(method => method === 'FREIGHT');

  // Open the appropriate terms modal for the active shipping methods (priority: pickup → customer freight → our freight)
  const openTermsModal = (formData?: CheckoutForm) => {
    if (formData) setPendingFormData(formData);
    if (hasLocalPickupSelected()) {
      setShowPickupConfirmModal(true);
      setPickupConfirmText("");
    } else if (hasCustomerFreightSelected()) {
      setShowCustomerFreightTermsModal(true);
      setCustomerFreightConfirmText("");
    } else {
      setShowFreightTermsModal(true);
      setFreightConfirmText("");
    }
  };

  // Actual B2B order submission (after confirmation if needed)
  const executeB2BSubmit = async () => {
    // Check for cart conflicts before creating order
    try {
      const conflicts = await checkConflicts();
      if (conflicts.removed.length > 0 || conflicts.adjusted.length > 0) {
        setCartConflicts(conflicts);
        return; // Stop submission, show modal
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to verify cart availability. Please try again.",
        variant: "destructive",
      });
      return;
    }

    // No conflicts, proceed with order creation
    // B2B users bypass form validation - dummy values
    createOrderMutation.mutate({
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      shippingAddress: "",
      shippingCity: "",
      shippingState: "",
      shippingZip: "",
      hasLoadingDock: false,
      hasForklift: false,
    });
  };

  const handleB2BSubmit = async () => {
    if (capacity.capacityUsedPercentage > 100) {
      toast({
        title: "Capacity Exceeded",
        description: "Your cart exceeds truck capacity. Please remove some items.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedAddressId) {
      toast({
        title: "Address Required",
        description: "Please select a shipping address",
        variant: "destructive",
      });
      return;
    }

    if (!poNumber || poNumber.trim() === "") {
      toast({
        title: "PO Number Required",
        description: "Please enter a purchase order number to continue",
        variant: "destructive",
      });
      return;
    }

    // Always show the appropriate terms modal before placing an order
    openTermsModal();
  };

  // Handle local pickup confirmation
  const handlePickupConfirm = async () => {
    if (pickupConfirmText.toLowerCase() !== "i agree") {
      toast({
        title: "Confirmation Required",
        description: "Please type 'I agree' to confirm the terms",
        variant: "destructive",
      });
      return;
    }

    setShowPickupConfirmModal(false);
    setPickupConfirmText("");
    
    // Check if this is a non-B2B submit (has pending form data) or B2B submit
    if (pendingFormData) {
      await executeNonB2BSubmit(pendingFormData);
      setPendingFormData(null);
    } else {
      await executeB2BSubmit();
    }
  };

  const handleFreightConfirm = async () => {
    if (freightConfirmText.toLowerCase() !== "i agree") {
      toast({
        title: "Confirmation Required",
        description: "Please type 'I agree' to confirm the terms",
        variant: "destructive",
      });
      return;
    }
    setShowFreightTermsModal(false);
    setFreightConfirmText("");
    if (pendingFormData) {
      await executeNonB2BSubmit(pendingFormData);
      setPendingFormData(null);
    } else {
      await executeB2BSubmit();
    }
  };

  const handleCustomerFreightConfirm = async () => {
    if (customerFreightConfirmText.toLowerCase() !== "i agree") {
      toast({
        title: "Confirmation Required",
        description: "Please type 'I agree' to confirm the terms",
        variant: "destructive",
      });
      return;
    }
    setShowCustomerFreightTermsModal(false);
    setCustomerFreightConfirmText("");
    if (pendingFormData) {
      await executeNonB2BSubmit(pendingFormData);
      setPendingFormData(null);
    } else {
      await executeB2BSubmit();
    }
  };

  const handleB2BAddressChange = (addressId: string) => {
    setSelectedAddressId(addressId);
    setAddressesExpanded(false); // Collapse after selection
  };

  const [addressesExpanded, setAddressesExpanded] = useState(false);

  // Helper interfaces for grouping
  interface ModelGroup {
    modelId: string;
    modelName: string;
    brand: string;
    description?: string;
    msrp?: string;
    imageUrl?: string;
    dimensions: { width: number; depth: number };
    /** model.categoryId snapshot — drives per-category grading copy +
     *  the pallet inventory pill at checkout (Task #156). */
    categoryId?: string | null;
    gradeGroups: {
      grade: 'A' | 'B+' | 'B-';
      items: typeof items;
      totalQty: number;
      totalPrice: number;
    }[];
  }

  interface WarehouseShipment {
    warehouseCode: string;
    city: string;
    region: string;
    items: typeof items;
    modelGroups: ModelGroup[];
    subtotal: number;
    freightQuotes: UnifiedCarrierQuote[] | null;
    // Local pickup info if available for this warehouse
    localPickupInfo: {
      available: boolean;
      fee: number;
      line1: string;
      line2?: string;
      city: string;
      region: string;
      postalCode: string;
    } | null;
  }

  // Fetch warehouse information
  // Group items by warehouse
  const getWarehouseShipments = (): WarehouseShipment[] => {
    const gradeOrder = { 'A': 0, 'B+': 1, 'B-': 2 };
    const warehouseMap = new Map<string, WarehouseShipment>();

    // Group items by warehouse using data from cart items
    items.forEach(item => {
      const warehouseCode = item.warehouseCode || 'UNKNOWN';

      if (!warehouseMap.has(warehouseCode)) {
        // Check if this warehouse supports local pickup
        const pickupWarehouse = localPickupWarehouses?.find(w => w.warehouseCode === warehouseCode);
        const localPickupInfo = pickupWarehouse ? {
          available: true,
          fee: pickupWarehouse.localPickupFee ? parseFloat(pickupWarehouse.localPickupFee) : 0,
          line1: pickupWarehouse.line1,
          line2: pickupWarehouse.line2 || undefined,
          city: pickupWarehouse.city,
          region: pickupWarehouse.region,
          postalCode: pickupWarehouse.postalCode,
        } : null;

        warehouseMap.set(warehouseCode, {
          warehouseCode,
          city: item.city || 'Unknown',
          region: item.region || 'Unknown',
          items: [],
          modelGroups: [],
          subtotal: 0,
          freightQuotes: freightQuotes.get(warehouseCode) || null,
          localPickupInfo,
        });
      }

      const shipment = warehouseMap.get(warehouseCode)!;
      shipment.items.push(item);
      shipment.subtotal += multiplyMoney(item.price, item.quantity); // Use multiplyMoney for cents precision
    });

    // Group items by model within each warehouse
    warehouseMap.forEach(shipment => {
      const modelGroupsMap = new Map<string, ModelGroup>();

      shipment.items.forEach(item => {
        if (!modelGroupsMap.has(item.modelId)) {
          modelGroupsMap.set(item.modelId, {
            modelId: item.modelId,
            modelName: item.modelName,
            brand: item.brand,
            description: item.description,
            msrp: item.msrp,
            imageUrl: item.imageUrl,
            dimensions: item.dimensions,
            categoryId: item.categoryId ?? null,
            gradeGroups: [],
          });
        }

        const modelGroup = modelGroupsMap.get(item.modelId)!;
        let gradeGroup = modelGroup.gradeGroups.find(g => g.grade === item.grade);
        if (!gradeGroup) {
          gradeGroup = {
            grade: item.grade,
            items: [],
            totalQty: 0,
            totalPrice: 0,
          };
          modelGroup.gradeGroups.push(gradeGroup);
        }

        gradeGroup.items.push(item);
        gradeGroup.totalQty += item.quantity;
        gradeGroup.totalPrice += multiplyMoney(item.price, item.quantity); // Use multiplyMoney for cents precision
      });

      modelGroupsMap.forEach(modelGroup => {
        modelGroup.gradeGroups.sort((a, b) => gradeOrder[a.grade] - gradeOrder[b.grade]);
      });

      shipment.modelGroups = Array.from(modelGroupsMap.values());
    });

    return Array.from(warehouseMap.values());
  };

  if (orderCreated) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="border-success/50 bg-success/5">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 className="h-8 w-8 text-success" />
              <CardTitle className="text-success">Order Confirmed!</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-lg">
              Thank you for your order! Your order number is:{" "}
              <span className="font-bold text-primary">#{orderId.slice(-8).toUpperCase()}</span>
            </p>
            <p className="text-muted-foreground">
              You will receive an email confirmation shortly with tracking information.
            </p>
            <Separator />
            <div className="flex gap-4">
              <Button onClick={() => navigate("/")} data-testid="button-continue-shopping">
                Continue Shopping
              </Button>
              <Button variant="outline" onClick={() => navigate("/admin")} data-testid="button-view-orders">
                View Orders
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const capacityColor =
    capacity.capacityUsedPercentage >= 90 ? "destructive" :
    capacity.capacityUsedPercentage >= 70 ? "warning" : "success";

  const canCheckout = capacity.capacityUsedPercentage <= 100;

  // Every warehouse that is set to FREIGHT (explicit or default) must have loaded quotes.
  // LOCAL_PICKUP and CUSTOMER_FREIGHT warehouses are exempt.
  const allFreightReady = useMemo(() => {
    const warehouseCodes = Array.from(new Set(items.map(i => i.warehouseCode).filter((c): c is string => !!c)));
    if (warehouseCodes.length === 0) return true;
    return warehouseCodes.every(wc => {
      const method = shippingMethods.get(wc) || 'FREIGHT';
      if (method === 'LOCAL_PICKUP' || method === 'CUSTOMER_FREIGHT') return true;
      const quotes = freightQuotes.get(wc);
      return Array.isArray(quotes) && quotes.length > 0;
    });
  }, [items, shippingMethods, freightQuotes]);

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-8 lg:py-12">
      <div className="mb-4 sm:mb-6 lg:mb-8">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-1 sm:mb-2">Checkout</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">Review your order and complete purchase</p>
      </div>

      {/* Unauthenticated users are redirected to login by the useEffect above;
          this is just a safety fallback in case the redirect hasn't fired yet */}
      {!isAuthenticated && (
        <Card className="mb-6 border-primary/50 bg-primary/5">
          <CardContent className="py-4 text-center text-sm text-muted-foreground">
            Redirecting you to sign in…
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {/* Left: Cart Items / Inventory */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {(() => {
            const shipments = getWarehouseShipments();
            const showShipmentNumber = shipments.length > 1;

            return shipments.map((shipment, shipmentIndex) => {
            const selectedCarrierIndex = selectedCarriers.get(shipment.warehouseCode) ?? 0;
            const selectedQuote = shipment.freightQuotes?.[selectedCarrierIndex];
            // Use getShippingCostForWarehouse to respect shipping method (LOCAL_PICKUP vs FREIGHT)
            const shippingCost = getShippingCostForWarehouse(
              shipment.warehouseCode, 
              shipment.freightQuotes || null, 
              shipment.localPickupInfo || null
            );
            const shipmentTotal = shipment.subtotal + shippingCost;

            return (
              <Card 
                key={shipment.warehouseCode}
                className="bg-gray-50 dark:bg-gray-900/20"
              >
                {/* Shipment Header - Mobile responsive */}
                <CardHeader style={{ backgroundColor: '#9eba4a' }} className="text-[#000000] p-2 sm:p-4 py-1.5 sm:py-2">
                  <CardTitle className="tracking-tight flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm lg:text-[16px] font-normal">
                    <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 flex-shrink-0" />
                    <span className="truncate">{showShipmentNumber ? `Shipment ${shipmentIndex + 1} | ` : ''}{shipment.warehouseCode} | {shipment.city}, {shipment.region}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-2 sm:p-4 lg:p-6">
                  {(() => {
                    // Shipment-level date bucket computation
                    type GradeGroup = ModelGroup['gradeGroups'][number];
                    const allShipmentItems: CartItem[] = shipment.modelGroups.flatMap((m: ModelGroup) => m.gradeGroups.flatMap((g: GradeGroup) => g.items as CartItem[]));
                    const shipmentPresaleDates = [...new Set(
                      allShipmentItems.filter(i => i.expectedShipDate).map(i => i.expectedShipDate as string)
                    )].sort();
                    const shipmentHasPresale = shipmentPresaleDates.length > 0;
                    const shipmentHasInStock = shipmentHasPresale && allShipmentItems.some(i => !i.expectedShipDate);

                    // Filter shipment model groups to only items matching the bucket filter
                    const getBucketGroups = (itemFilter: (i: CartItem) => boolean): ModelGroup[] =>
                      shipment.modelGroups.map((mg: ModelGroup) => {
                        const filteredGGs = mg.gradeGroups.map((gg: GradeGroup) => {
                          const filteredItems: CartItem[] = (gg.items as CartItem[]).filter(itemFilter);
                          if (!filteredItems.length) return null;
                          return {
                            ...gg,
                            items: filteredItems,
                            totalQty: filteredItems.reduce((s: number, i: CartItem) => s + i.quantity, 0),
                            totalPrice: filteredItems.reduce((s: number, i: CartItem) => s + i.price * i.quantity, 0),
                          };
                        }).filter((g): g is GradeGroup => g !== null);
                        if (!filteredGGs.length) return null;
                        return { ...mg, gradeGroups: filteredGGs };
                      }).filter((mg): mg is ModelGroup => mg !== null);

                    // Render model groups (date bucketing moved to shipment level)
                    const renderModelGroupList = (modelGroupsToRender: ModelGroup[]) => (
                      <div className="space-y-4 sm:space-y-6">
                        {modelGroupsToRender.map((modelGroup: ModelGroup) => {
                          const totalModelUnits = modelGroup.gradeGroups.reduce((sum: number, g: GradeGroup) => sum + g.totalQty, 0);
                          const totalModelPrice = modelGroup.gradeGroups.reduce((sum: number, g: GradeGroup) => sum + g.totalPrice, 0);
                          return (
                            <div key={modelGroup.modelId} className="border border-border rounded-lg overflow-hidden">
                              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 lg:gap-6 p-2 sm:p-3 lg:p-4">
                                <div className="flex-shrink-0 flex justify-center sm:justify-start">
                                  <Link href={`/product/${modelGroup.modelId}`}>
                                    {modelGroup.imageUrl ? (
                                      <img src={modelGroup.imageUrl} alt={modelGroup.modelName} className="w-24 h-24 sm:w-32 sm:h-auto md:w-40 lg:w-48 max-h-[120px] sm:max-h-[160px] lg:max-h-[200px] object-contain rounded border border-border cursor-pointer hover:opacity-80 transition-opacity" data-testid={`img-model-${modelGroup.modelId}`} />
                                    ) : (
                                      <div className="w-24 h-24 sm:w-32 md:w-40 lg:w-48 sm:h-32 lg:h-40 bg-secondary/20 rounded border border-border flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                                        <span className="text-[10px] sm:text-xs text-muted-foreground">No image</span>
                                      </div>
                                    )}
                                  </Link>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="mb-2 sm:mb-3 lg:mb-4 pb-2 sm:pb-3 lg:pb-4 border-b border-border">
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                                      <div className="flex-1 min-w-0">
                                        <Link href={`/product/${modelGroup.modelId}`}>
                                          <p className="font-bold text-sm sm:text-base lg:text-lg text-foreground cursor-pointer hover:text-primary transition-colors">
                                            {modelGroup.brand} {modelGroup.modelName}
                                          </p>
                                        </Link>
                                        {modelGroup.description && (
                                          <p className="text-[11px] sm:text-xs lg:text-sm text-muted-foreground line-clamp-2 mt-0.5 sm:mt-1 hidden sm:block">{modelGroup.description}</p>
                                        )}
                                        <div className="hidden sm:flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-muted-foreground mt-1 sm:mt-2">
                                          {modelGroup.dimensions.width > 0 && modelGroup.dimensions.depth > 0 && (
                                            <span>Dimensions: {modelGroup.dimensions.width}" × {modelGroup.dimensions.depth}"</span>
                                          )}
                                          {modelGroup.msrp && <span>MSRP: {formatMoney(parsePrice(modelGroup.msrp))}</span>}
                                        </div>
                                      </div>
                                      <div className="text-left sm:text-right flex sm:flex-col items-center sm:items-end gap-2 sm:gap-0 flex-shrink-0">
                                        <p className="text-[10px] sm:text-xs text-muted-foreground">Total Units</p>
                                        <p className="text-xl sm:text-2xl font-bold text-foreground">{totalModelUnits}</p>
                                        <p className="text-xs sm:text-sm font-semibold text-foreground sm:mt-1">{formatMoney(totalModelPrice)}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                                    {modelGroup.gradeGroups.map((gradeGroup: GradeGroup) => {
                                      const ggItems = gradeGroup.items as CartItem[];
                                      return (
                                      <div key={`${modelGroup.modelId}-${gradeGroup.grade}`} className="pb-2 sm:pb-3 lg:pb-4 border-b border-border last:border-b-0 last:pb-0">
                                        <div className="flex items-start justify-between mb-1.5 sm:mb-2 lg:mb-3">
                                          <div className="flex items-center gap-2 sm:gap-3">
                                            {ggItems[0]?.isFactoryRefurbished ? (
                                              <Link
                                                href={FACTORY_REFURBISHED_GRADING_ANCHOR}
                                                className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border-2 border-emerald-500 bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/40 transition-colors cursor-pointer"
                                                data-testid={`cart-badge-factory-refurbished-${modelGroup.modelId}-${gradeGroup.grade}`}
                                                title="Factory-refurbished by the manufacturer to like-new spec — click to learn more"
                                              >
                                                <Sparkles className="h-3 w-3" />
                                                {FACTORY_REFURBISHED_LABEL}
                                              </Link>
                                            ) : (
                                              <GradeBadge grade={gradeGroup.grade} compact category={lookupGradeBadgeCategory(modelGroup.categoryId)} />
                                            )}
                                            {(() => {
                                              const todayYmd = new Date().toISOString().slice(0, 10);
                                              const dates = Array.from(new Set(ggItems.map(i => i.expectedShipDate).filter((d): d is string => !!d && d > todayYmd)));
                                              if (dates.length === 0) return null;
                                              if (dates.length === 1) {
                                                const slot = getShipDateColor(dates[0], checkoutShipDateColorMap);
                                                const label = (() => { try { return new Date(dates[0] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return dates[0]; } })();
                                                return (
                                                  <span
                                                    className={`inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 rounded ${slot.bannerBg} ${slot.text}`}
                                                    data-testid={`checkout-ship-date-${modelGroup.modelId}-${gradeGroup.grade}`}
                                                  >
                                                    <Clock className="h-2.5 w-2.5" />
                                                    Ships {label}
                                                  </span>
                                                );
                                              }
                                              return (
                                                <span
                                                  className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 rounded bg-amber-500 text-white"
                                                  title={dates.join(' / ')}
                                                  data-testid={`checkout-ship-date-mixed-${modelGroup.modelId}-${gradeGroup.grade}`}
                                                >
                                                  <Clock className="h-2.5 w-2.5" />
                                                  Multiple dates
                                                </span>
                                              );
                                            })()}
                                          </div>
                                          <p className="text-xs sm:text-sm lg:text-base font-semibold text-foreground">{formatMoney(gradeGroup.totalPrice)}</p>
                                        </div>
                                        {/* Items rendered directly — date bucketing is at shipment level */}
                                        <div className="space-y-0.5 sm:space-y-1 mt-1 sm:mt-1.5">
                                          {ggItems.map((item: CartItem) => (
                                            <div key={item.id} className="flex items-center py-0.5 px-1.5 sm:p-1 bg-secondary/30 rounded" onClick={() => { if (pendingDeleteItemId === item.id) { setPendingDeleteItemId(null); } }}>
                                              {item.serialNumber ? (
                                                <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                                                  {item.videoUrl ? (
                                                    <button type="button" className="text-[10px] sm:text-xs font-mono text-muted-foreground hover:text-primary hover:underline cursor-pointer flex items-center gap-1 truncate touch-target" onClick={(e) => { e.stopPropagation(); setSelectedVideoItem({ serialNumber: item.serialNumber!, videoUrl: normalizeVideoUrl(item.videoUrl) || '', grade: gradeGroup.grade }); }} data-testid={`link-video-${item.serialNumber}`}>
                                                      <span className="truncate">SN: {item.serialNumber}</span>
                                                      <Video className="h-3 w-3 flex-shrink-0" />
                                                    </button>
                                                  ) : (
                                                    <span className="text-[10px] sm:text-xs font-mono text-muted-foreground truncate">SN: {item.serialNumber}</span>
                                                  )}
                                                  <button type="button" className="p-1.5 sm:p-1 text-red-500 hover:text-red-700 touch-target flex-shrink-0" onClick={(e) => { e.stopPropagation(); if (pendingDeleteItemId === item.id) { removeItem(item.id); toast({ title: "Item removed", description: `${item.brand} ${item.modelName} has been removed from your cart.` }); setPendingDeleteItemId(null); } else { setPendingDeleteItemId(item.id); } }} data-testid={`button-remove-${item.id}`}>
                                                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                                  </button>
                                                  {pendingDeleteItemId === item.id && (
                                                    <span className="text-[10px] sm:text-xs font-bold whitespace-nowrap" style={{ color: '#0014ff' }}>&lt; Click again to confirm</span>
                                                  )}
                                                </div>
                                              ) : (
                                                <>
                                                  <div className="flex-1 min-w-0" />
                                                  <div className="flex items-center gap-1 sm:gap-2 ml-2 sm:ml-4">
                                                    {editingItemId === item.id ? (
                                                      <div className="flex flex-wrap items-center gap-1">
                                                        <Button size="sm" variant="outline" className="h-6 w-6 sm:h-7 sm:w-7 p-0 touch-target" onClick={() => setEditingQuantity(Math.max(0, editingQuantity - 1))} data-testid={`button-decrease-qty-${item.id}`}>
                                                          <Minus className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                                                        </Button>
                                                        <input type="text" inputMode="numeric" value={editingQuantity} onChange={(e) => { const numValue = parseInt(e.target.value) || 0; const maxQty = item.maxQuantity ?? Infinity; setEditingQuantity(Math.min(Math.max(0, numValue), maxQty)); }} className="w-10 sm:w-12 h-6 sm:h-7 text-center text-xs sm:text-sm border border-border rounded px-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-testid={`input-qty-${item.id}`} autoFocus />
                                                        <Button size="sm" variant="outline" className="h-6 w-6 sm:h-7 sm:w-7 p-0 touch-target" onClick={() => { const maxQty = item.maxQuantity ?? Infinity; if (editingQuantity < maxQty) { setEditingQuantity(editingQuantity + 1); } }} disabled={item.maxQuantity !== undefined && editingQuantity >= item.maxQuantity} data-testid={`button-increase-qty-${item.id}`}>
                                                          <Plus className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                                                        </Button>
                                                        <Button size="sm" variant="default" className="h-6 sm:h-7 px-1.5 sm:px-2 text-xs touch-target" onClick={async () => { if (editingQuantity <= 0) { await removeItem(item.id); } else { await updateQuantity(item.id, editingQuantity); } setEditingItemId(null); setEditingQuantity(0); }} data-testid={`button-save-qty-${item.id}`}>
                                                          Save
                                                        </Button>
                                                        <Button size="sm" variant="outline" className="h-6 sm:h-7 px-1.5 sm:px-2 text-xs touch-target" onClick={() => { setEditingItemId(null); setEditingQuantity(0); }} data-testid={`button-cancel-edit-${item.id}`}>
                                                          Cancel
                                                        </Button>
                                                      </div>
                                                    ) : (
                                                      <>
                                                        <span className="text-sm font-medium min-w-fit">Qty: {item.quantity}</span>
                                                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { setEditingItemId(item.id); setEditingQuantity(item.quantity); }} data-testid={`button-edit-qty-${item.id}`}>
                                                          Edit
                                                        </Button>
                                                        <button type="button" className="p-1 text-red-500 hover:text-red-700" onClick={(e) => { e.stopPropagation(); if (pendingDeleteItemId === item.id) { removeItem(item.id); toast({ title: "Item removed", description: `${item.brand} ${item.modelName} has been removed from your cart.` }); setPendingDeleteItemId(null); } else { setPendingDeleteItemId(item.id); } }} data-testid={`button-remove-${item.id}`}>
                                                          <Trash2 className="h-4 w-4" />
                                                        </button>
                                                        {pendingDeleteItemId === item.id && (
                                                          <span className="text-xs font-bold" style={{ color: '#0014ff' }}>&lt; Click again to confirm</span>
                                                        )}
                                                      </>
                                                    )}
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );

                    if (!shipmentHasPresale) {
                      return renderModelGroupList(shipment.modelGroups);
                    }

                    return (
                      <>
                        {shipmentHasInStock && (
                          <div className="mb-3">
                            <div className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-800 bg-green-100 dark:text-green-200 dark:bg-green-900/40 rounded mb-2">
                              <CheckCircle2 className="w-3 h-3 shrink-0" />
                              <span>Available now</span>
                            </div>
                            {renderModelGroupList(getBucketGroups(i => !i.expectedShipDate))}
                          </div>
                        )}
                        {shipmentPresaleDates.map((date: string) => {
                          const colorSlot = getShipDateColor(date, checkoutShipDateColorMap);
                          const label = (() => { try { return new Date(date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return date; } })();
                          return (
                            <div key={date} className="mb-3">
                              <div className={`flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded mb-2 ${colorSlot.bannerBg}`}>
                                <Clock className="w-3 h-3 shrink-0" />
                                <span>Arriving {label}</span>
                              </div>
                              {renderModelGroupList(getBucketGroups(i => i.expectedShipDate === date))}
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}

                  {/* Shipment Summary */}
                  <Separator className="my-4" />
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>{formatMoney(shipment.subtotal)}</span>
                    </div>

                    {freightQuotesLoading && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Truck className="h-4 w-4 animate-pulse" />
                        <span>Calculating shipping...</span>
                      </div>
                    )}

                    {/* Display selected carrier (if any) */}
                    {shipment.freightQuotes && shipment.freightQuotes.length > 0 && !freightQuotesLoading && (() => {
                      const quote = shipment.freightQuotes[selectedCarrierIndex];
                      if (!quote) return null;
                      return (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{quote.carrier}</span>
                          <span>{formatMoney(quote.totalCost)}</span>
                        </div>
                      );
                    })()}

                    <Separator />
                    <div className="flex justify-between text-base font-bold pt-1">
                      <span>Shipment Total</span>
                      <span>{formatMoney(shipmentTotal)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
            });
          })()}
        </div>

        {/* Right: Address Selection & Checkout */}
        <div className="lg:col-span-1">
          {isB2BUser ? (
            /* B2B User View */
            (<>
              <Card className="mb-4" data-testid="card-company-info">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="h-5 w-5" />
                    {userAccount?.companyName}
                  </CardTitle>
                </CardHeader>
              </Card>
              {/* Collapsible Address Selector */}
              <Card className="mb-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Shipping Address</CardTitle>
                    <Link href="/account#addresses" className="text-sm text-primary hover:underline" data-testid="link-edit-addresses">
                      Edit
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  {accountAddresses && accountAddresses.length > 0 ? (
                    <>
                      {/* Selected Address Display */}
                      {!addressesExpanded && selectedAddressId && (() => {
                        const selectedAddr = accountAddresses.find(addr => addr.id === selectedAddressId);
                        return selectedAddr ? (
                          <div 
                            className="p-3 bg-primary/5 border border-primary rounded-lg cursor-pointer hover:bg-primary/10 transition-colors"
                            onClick={() => setAddressesExpanded(true)}
                            data-testid="selected-address-display"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <MapPin className="h-4 w-4 text-muted-foreground" />
                                  <p className="font-semibold text-sm">{selectedAddr.locationName}</p>
                                </div>
                                <div className="text-xs text-muted-foreground space-y-0.5 ml-6">
                                  <p>{selectedAddr.line1}</p>
                                  {selectedAddr.line2 && <p>{selectedAddr.line2}</p>}
                                  <p>{selectedAddr.city}, {selectedAddr.region} {selectedAddr.postalCode}</p>
                                </div>
                                <div className="flex gap-1 mt-2 ml-6">
                                  {selectedAddr.hasLoadingDock && (
                                    <Badge variant="outline" className="text-xs">
                                      <Truck className="h-3 w-3 mr-1" />
                                      Dock
                                    </Badge>
                                  )}
                                  {selectedAddr.hasForklift && (
                                    <Badge variant="outline" className="text-xs">
                                      Forklift
                                    </Badge>
                                  )}
                                  {!selectedAddr.hasLoadingDock && !selectedAddr.hasForklift && (
                                    <Badge variant="destructive" className="text-xs">
                                      Liftgate Service Required
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                            </div>
                          </div>
                        ) : null;
                      })()}

                      {/* Expanded Address List */}
                      {addressesExpanded && (
                        <div className="space-y-2">
                          {accountAddresses.filter(addr => addr.isShipping).map((address) => (
                            <div
                              key={address.id}
                              className={`p-3 rounded-lg cursor-pointer transition-all ${
                                selectedAddressId === address.id 
                                  ? 'border-2 border-primary bg-primary/5' 
                                  : 'border border-border hover:border-primary/50 bg-secondary/20'
                              }`}
                              onClick={() => handleB2BAddressChange(address.id)}
                              data-testid={`address-option-${address.id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <MapPin className="h-4 w-4 text-muted-foreground" />
                                    <p className="font-semibold text-sm">{address.locationName}</p>
                                    {address.isDefaultShipping && (
                                      <Badge variant="default" className="text-xs">Default</Badge>
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground space-y-0.5 ml-6">
                                    <p>{address.line1}</p>
                                    {address.line2 && <p>{address.line2}</p>}
                                    <p>{address.city}, {address.region} {address.postalCode}</p>
                                  </div>
                                  <div className="flex gap-1 mt-2 ml-6">
                                    {address.hasLoadingDock && (
                                      <Badge variant="outline" className="text-xs">
                                        <Truck className="h-3 w-3 mr-1" />
                                        Dock
                                      </Badge>
                                    )}
                                    {address.hasForklift && (
                                      <Badge variant="outline" className="text-xs">
                                        Forklift
                                      </Badge>
                                    )}
                                    {!address.hasLoadingDock && !address.hasForklift && (
                                      <Badge variant="destructive" className="text-xs">
                                        Liftgate Service Required
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                {selectedAddressId === address.id && (
                                  <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <MapPin className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">No shipping addresses available</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Contact your administrator
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>)
          ) : (
            /* Regular User View - Form Fields */
            (<>
              {!isAuthenticated || !savedAddresses || savedAddresses.length === 0 || !selectedAddressId || selectedAddressId === "new" ? (
              <Card>
                <CardHeader>
                  <CardTitle>Customer Information</CardTitle>
                </CardHeader>
                <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="customerName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full Name</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="input-customer-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="customerEmail"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input type="email" {...field} data-testid="input-customer-email" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="customerPhone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone</FormLabel>
                            <FormControl>
                              <Input type="tel" {...field} data-testid="input-customer-phone" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator />

                    <FormField
                      control={form.control}
                      name="shippingAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Shipping Address</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="input-shipping-address" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="shippingCity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>City</FormLabel>
                            <FormControl>
                              <Input {...field} data-testid="input-shipping-city" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shippingState"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>State</FormLabel>
                            <FormControl>
                              <Input {...field} data-testid="input-shipping-state" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shippingZip"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>ZIP Code</FormLabel>
                            <FormControl>
                              <Input {...field} data-testid="input-shipping-zip" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <h3 className="text-sm font-medium flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Delivery Options
                      </h3>
                      <FormField
                        control={form.control}
                        name="hasLoadingDock"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                data-testid="checkbox-loading-dock"
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>
                                Loading Dock Available
                              </FormLabel>
                              <FormDescription>
                                Check if your location has a loading dock for receiving freight
                              </FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="hasForklift"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                data-testid="checkbox-forklift"
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>
                                Forklift Available
                              </FormLabel>
                              <FormDescription>
                                Check if you have a forklift for unloading heavy items
                              </FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                      <p className="text-xs text-muted-foreground">
                        If neither option is checked, a liftgate service fee will be automatically added to your freight quote.
                      </p>
                    </div>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
              ) : null}
            </>)
          )}

          {/* Order Summary - Shared for both user types - Mobile responsive */}
          <Card className="sticky top-4">
            <CardHeader className="p-3 sm:p-4 lg:p-6 pt-0 pb-0">
              <CardTitle className="text-[20px] sm:text-base mt-[10px]">Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-4 lg:p-6 pt-0">

              {/* Total */}
              <div className="space-y-1.5 sm:space-y-2">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="font-bold text-[#212121]">Subtotal</span>
                  <span className="font-semibold">{formatMoney(total)}</span>
                </div>

                {/* Freight Quote Section - Mobile responsive */}
                {freightQuotesLoading && (
                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground">
                    <Truck className="h-3 w-3 sm:h-4 sm:w-4 animate-pulse flex-shrink-0" />
                    <span>Calculating shipping...</span>
                  </div>
                )}

                {/* Admin-only: inspect the exact freight request/response payload */}
                {isAdminUser && freightDebug.size > 0 && !freightQuotesLoading && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="w-full justify-center gap-2 border-2 border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-500 dark:bg-amber-950 dark:text-amber-100 dark:hover:bg-amber-900 font-medium"
                    onClick={() => setFreightDebugOpen(true)}
                    data-testid="button-freight-debug"
                  >
                    <Bug className="h-4 w-4" />
                    View Freight Payload (Admin)
                  </Button>
                )}

                {freightQuotesError && (
                  <div className="flex items-center justify-between gap-2 p-2 rounded bg-destructive/10 border border-destructive/20">
                    <span className="text-[10px] sm:text-xs text-destructive">{freightQuotesError}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/20"
                      onClick={retryFreightQuotes}
                      data-testid="button-retry-freight"
                    >
                      Retry
                    </Button>
                  </div>
                )}

                {(freightQuotes.size > 0 || (localPickupWarehouses && localPickupWarehouses.length > 0)) && !freightQuotesLoading && (
                  <div className="space-y-1.5 sm:space-y-2 pt-1.5 sm:pt-2">
                    {(() => {
                      const shipmentData = getWarehouseShipments();
                      
                      // Pre-calculate shipping costs for each shipment
                      const shipmentCosts = shipmentData.map(shipment => {
                        const method = shippingMethods.get(shipment.warehouseCode) || 'FREIGHT';
                        const isPickup = method === 'LOCAL_PICKUP' && shipment.localPickupInfo?.available;
                        const isCustomerFreight = method === 'CUSTOMER_FREIGHT';
                        let cost = 0;
                        
                        let carrierName: string | null = null;
                        if (isPickup && shipment.localPickupInfo) {
                          cost = shipment.localPickupInfo.fee;
                        } else if (isCustomerFreight) {
                          cost = 0;
                        } else if (shipment.freightQuotes && shipment.freightQuotes.length > 0) {
                          const selectedIndex = selectedCarriers.get(shipment.warehouseCode) ?? 0;
                          if (shipment.freightQuotes[selectedIndex]) {
                            cost = shipment.freightQuotes[selectedIndex].totalCost;
                            carrierName = shipment.freightQuotes[selectedIndex].carrier;
                          }
                        }
                        
                        return { shipment, isPickup, isCustomerFreight, carrierName, cost };
                      });
                      
                      const totalShipping = shipmentCosts.reduce((sum, item) => sum + item.cost, 0);
                      
                      return (
                        <>
                          {/* Show each shipment's shipping method */}
                          {shipmentCosts.map(({ shipment, isPickup, isCustomerFreight, carrierName, cost }) => (
                            <div key={shipment.warehouseCode} className="flex items-center justify-between gap-1.5 sm:gap-2">
                              <div className="flex items-center gap-1.5">
                                {isPickup || isCustomerFreight ? (
                                  <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-[#c41e3a] flex-shrink-0" />
                                ) : (
                                  <Truck className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                                )}
                                <span className={`text-[10px] sm:text-xs font-medium ${isPickup || isCustomerFreight ? 'text-[#c41e3a]' : ''}`}>
                                  {isPickup && shipment.localPickupInfo
                                    ? `Pickup in ${shipment.localPickupInfo.city}, ${shipment.localPickupInfo.region}`
                                    : isCustomerFreight
                                    ? `Book my own freight from ${shipment.city}, ${shipment.region}`
                                    : carrierName
                                    ? `${carrierName}${shipmentCosts.length > 1 ? ` (${shipment.city}, ${shipment.region})` : ''}`
                                    : `Freight${shipmentCosts.length > 1 ? ` (${shipment.city}, ${shipment.region})` : ''}`
                                  }
                                </span>
                              </div>
                              <span className={`text-[10px] sm:text-xs font-medium ${isPickup || isCustomerFreight ? 'text-[#c41e3a]' : ''}`}>
                                {formatMoney(cost)}
                              </span>
                            </div>
                          ))}
                          <div className="flex justify-between text-xs sm:text-sm font-semibold pt-1">
                            <span>Shipping Total</span>
                            <span>{formatMoney(totalShipping)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {freightQuotes.size === 0 && !freightQuotesLoading && !freightQuotesError && (() => {
                  let city: string | undefined;
                  let state: string | undefined;
                  let zip: string | undefined;
                  let message: string;

                  // B2B users with account addresses
                  if (isB2BUser) {
                    if (!selectedAddressId && accountAddresses && accountAddresses.length > 0) {
                      message = "Select an address";
                    } else if (selectedAddressId) {
                      const address = accountAddresses?.find(addr => addr.id === selectedAddressId);
                      city = address?.city;
                      state = address?.region;
                      zip = address?.postalCode;

                      const missingFields = [];
                      if (!city) missingFields.push("city");
                      if (!state) missingFields.push("state");
                      if (!zip) missingFields.push("zip");

                      if (missingFields.length === 0) return null;
                      message = `Enter ${missingFields.join(", ")} to calculate`;
                    } else {
                      message = "No addresses on file";
                    }
                  } 
                  // Regular logged-in users with saved addresses
                  else if (isAuthenticated && savedAddresses && savedAddresses.length > 0 && (!selectedAddressId || selectedAddressId === "new")) {
                    // If they have saved addresses but are entering new or haven't selected
                    city = form.watch("shippingCity");
                    state = form.watch("shippingState");
                    zip = form.watch("shippingZip");

                    const missingFields = [];
                    if (!city) missingFields.push("city");
                    if (!state) missingFields.push("state");
                    if (!zip) missingFields.push("zip");

                    if (missingFields.length === 0) return null;

                    // If all fields are missing and they have saved addresses, prompt to select
                    if (missingFields.length === 3) {
                      message = "Select an address or enter new";
                    } else {
                      message = `Enter ${missingFields.join(", ")} to calculate`;
                    }
                  }
                  // Guest users or users without saved addresses
                  else {
                    city = form.watch("shippingCity");
                    state = form.watch("shippingState");
                    zip = form.watch("shippingZip");

                    const missingFields = [];
                    if (!city) missingFields.push("city");
                    if (!state) missingFields.push("state");
                    if (!zip) missingFields.push("zip");

                    if (missingFields.length === 0) return null;
                    message = `Enter ${missingFields.join(", ")} to calculate`;
                  }

                  return (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Shipping</span>
                      <span className="text-amber-600 dark:text-amber-500 text-xs" data-testid="freight-warning-checkout">
                        {message}
                      </span>
                    </div>
                  );
                })()}

                {/* Sales Tax */}
                {salesTax && (
                  <>
                    <div className="flex justify-between text-sm mt-2">
                      <span className="text-muted-foreground">Sales Tax ({(salesTax.taxRate * 100).toFixed(2)}%)</span>
                      <span className="font-medium" data-testid="checkout-sales-tax">
                        {formatMoney(salesTax.taxAmount)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Minnesota sales tax includes freight
                    </p>
                  </>
                )}

                {/* Tax Exempt Message */}
                {isAuthenticated && userAccount && userAccount.salesTaxExemptUrl && (
                  <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-500 mt-2">
                    <CheckCircle2 className="h-3 w-3" />
                    <span>Tax Exempt (W9 on file)</span>
                  </div>
                )}

                {/* Store Credit Panel */}
                {isAuthenticated && (storeCreditBalance?.availableBalance ?? 0) > 0 && (
                  <div className="mt-2 p-2.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <CreditCard className="h-3.5 w-3.5 text-green-600" />
                        <span className="text-xs font-medium text-green-800 dark:text-green-300">Store Credit Available</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-green-700 dark:text-green-400" data-testid="text-store-credit-balance">
                          {formatMoney(storeCreditBalance!.availableBalance)}
                        </span>
                        {appliedStoreCredit <= 0 && (
                          <button
                            type="button"
                            className="text-[10px] text-green-700 dark:text-green-400 underline"
                            onClick={() => setStoreCreditPanelOpen(o => !o)}
                            data-testid="button-toggle-store-credit"
                          >
                            {storeCreditPanelOpen ? 'Hide' : 'Apply'}
                          </button>
                        )}
                      </div>
                    </div>
                    {appliedStoreCredit > 0 ? (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-green-700 dark:text-green-400">Applied</span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-green-700 dark:text-green-400" data-testid="text-store-credit-applied">
                            -{formatMoney(appliedStoreCredit)}
                          </span>
                          <button
                            type="button"
                            className="text-[10px] text-red-500 hover:text-red-700 underline"
                            onClick={() => { setAppliedStoreCredit(0); setStoreCreditInput(""); setStoreCreditPanelOpen(false); }}
                            data-testid="button-remove-store-credit"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : storeCreditPanelOpen ? (
                      <div className="flex items-center gap-1.5">
                        <div className="relative flex-1">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            max={storeCreditBalance!.availableBalance}
                            placeholder="0.00"
                            value={storeCreditInput}
                            onChange={(e) => setStoreCreditInput(e.target.value)}
                            className="w-full pl-5 pr-2 py-1 text-xs border border-green-300 dark:border-green-700 rounded bg-white dark:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-green-500"
                            data-testid="input-store-credit-amount"
                          />
                        </div>
                        <button
                          type="button"
                          className="px-2.5 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded font-medium"
                          onClick={() => {
                            const val = parseFloat(storeCreditInput);
                            if (!val || val <= 0) return;
                            const grossOrderTotal = total + selectedFreightTotal + (salesTax?.taxAmount || 0);
                            const maxCredit = Math.min(val, storeCreditBalance!.availableBalance, grossOrderTotal);
                            setAppliedStoreCredit(maxCredit);
                            setStoreCreditInput(maxCredit.toFixed(2));
                          }}
                          data-testid="button-apply-store-credit"
                        >
                          Apply
                        </button>
                        <button
                          type="button"
                          className="px-2 py-1 text-xs border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 rounded hover:bg-green-100 dark:hover:bg-green-900/40"
                          onClick={() => {
                            const grossOrderTotal = total + selectedFreightTotal + (salesTax?.taxAmount || 0);
                            const max = Math.min(storeCreditBalance!.availableBalance, grossOrderTotal);
                            setAppliedStoreCredit(max);
                            setStoreCreditInput(max.toFixed(2));
                          }}
                          data-testid="button-apply-max-store-credit"
                        >
                          Max
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Applied store credit line item */}
                {appliedStoreCredit > 0 && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                      <CreditCard className="h-3.5 w-3.5" />Store Credit
                    </span>
                    <span className="font-semibold text-green-600 dark:text-green-400">-{formatMoney(appliedStoreCredit)}</span>
                  </div>
                )}

                <Separator />
                <div className="flex justify-between text-base sm:text-lg font-bold">
                  <span>Total</span>
                  <span className="text-primary" data-testid="checkout-grand-total">
                    {(() => {
                      let totalShipping = 0;
                      const shipmentData = getWarehouseShipments();
                      shipmentData.forEach(shipment => {
                        const method = shippingMethods.get(shipment.warehouseCode) || 'FREIGHT';
                        if (method === 'LOCAL_PICKUP' && shipment.localPickupInfo?.available) {
                          totalShipping += shipment.localPickupInfo.fee;
                        } else if (shipment.freightQuotes && shipment.freightQuotes.length > 0) {
                          const selectedIndex = selectedCarriers.get(shipment.warehouseCode) ?? 0;
                          if (shipment.freightQuotes[selectedIndex]) {
                            totalShipping += shipment.freightQuotes[selectedIndex].totalCost;
                          }
                        }
                      });
                      const grossTotal = total + totalShipping + (salesTax?.taxAmount || 0);
                      const netTotal = Math.max(0, grossTotal - appliedStoreCredit);
                      return formatMoney(netTotal);
                    })()}
                  </span>
                </div>
              </div>

              <Separator />

              {/* PO Number Field - Required for B2B - Near Place Order button */}
              {isB2BUser && (
                <div className="space-y-2">
                  <label htmlFor="po-number" className="text-sm font-medium flex items-center gap-1">
                    PO Number <span className="text-destructive">*</span>
                  </label>
                  <Input
                    id="po-number"
                    data-testid="input-po-number"
                    value={poNumber}
                    onChange={(e) => setPoNumber(e.target.value)}
                    onBlur={handlePoNumberBlur}
                    placeholder="Enter your purchase order number"
                    required
                    className={`text-sm ${poNumber.trim() ? 'border-green-500 focus:border-green-500 focus:ring-green-500' : 'border-red-500 focus:border-red-500 focus:ring-red-500'}`}
                  />
                </div>
              )}

              {/* Place Order Button - Mobile responsive */}
              {!canCheckout && (
                <div className="p-2 sm:p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-1.5 sm:gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-destructive">Capacity Exceeded</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">
                      Remove items to continue
                    </p>
                  </div>
                </div>
              )}

              {!allFreightReady && canCheckout && (
                <div className="p-2 sm:p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-600 rounded-lg flex items-start gap-1.5 sm:gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-amber-800 dark:text-amber-200">Freight Required</p>
                    <p className="text-[10px] sm:text-xs text-amber-700 dark:text-amber-300">
                      {freightQuotesLoading
                        ? "Loading freight quotes — please wait before placing your order."
                        : "A freight option must be selected for all shipments. Select a carrier above or choose local pickup."}
                    </p>
                  </div>
                </div>
              )}

              {/* Minimum order quantity warning */}
              {isB2BUser && !minimumOrderMet && (
                <div className="p-2 sm:p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-600 rounded-lg flex items-start gap-1.5 sm:gap-2" data-testid="alert-minimum-order-checkout">
                  <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-amber-800 dark:text-amber-200">Minimum Order Not Met</p>
                    <p className="text-[10px] sm:text-xs text-amber-700 dark:text-amber-300">
                      Your account requires a minimum of <strong>{minOrderQty} units</strong>. You have <strong>{totalCartQty}</strong>. Add {minOrderQty - totalCartQty} more unit{minOrderQty - totalCartQty !== 1 ? 's' : ''} to place your order.
                    </p>
                  </div>
                </div>
              )}

              {isB2BUser ? (
                <Button
                  onClick={handleB2BSubmit}
                  className="w-full text-[#000000] text-sm sm:text-base h-9 sm:h-10"
                  disabled={userIsInactive || !canCheckout || !allFreightReady || !selectedAddressId || createOrderMutation.isPending || isCheckingConflicts || !minimumOrderMet}
                  data-testid="button-place-order"
                >
                  <ShoppingCart className="h-4 w-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span className="truncate">{userIsInactive ? "Account inactive" : isCheckingConflicts ? "Checking..." : createOrderMutation.isPending ? "Processing..." : "Place Order"}</span>
                </Button>
              ) : (
                <Button
                  onClick={form.handleSubmit(onSubmit)}
                  className="w-full text-[#000000] text-sm sm:text-base h-9 sm:h-10"
                  disabled={userIsInactive || !canCheckout || !allFreightReady || createOrderMutation.isPending || isCheckingConflicts}
                  data-testid="button-place-order"
                >
                  <ShoppingCart className="h-4 w-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span className="truncate">{userIsInactive ? "Account inactive" : isCheckingConflicts ? "Checking..." : createOrderMutation.isPending ? "Processing..." : "Place Order"}</span>
                </Button>
              )}
              {userIsInactive && (
                <div
                  className="mt-2 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 px-3 py-2 text-sm text-red-800 dark:text-red-200"
                  data-testid="alert-checkout-account-inactive"
                >
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    Your account is locked and can't place orders right now. Please contact us to reactivate it.
                  </span>
                </div>
              )}

              {/* Shipping Address Selector for Regular Users - below Place Order */}
              {!isB2BUser && isAuthenticated && savedAddresses && savedAddresses.length > 0 && (
                <div className="mt-4">
                  {/* Inline display when address is selected and not expanded */}
                  {selectedAddressId && selectedAddressId !== "new" && !addressesExpanded ? (
                    <div 
                      className="p-3 bg-secondary/30 rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors"
                      onClick={() => setAddressesExpanded(true)}
                      data-testid="inline-address-display"
                    >
                      {(() => {
                        const addr = savedAddresses.find(a => a.id === selectedAddressId);
                        if (!addr) return <span className="text-sm text-muted-foreground">Select address</span>;
                        return (
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2">
                              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                              <div className="space-y-0.5">
                                <p className="text-sm font-semibold text-foreground">
                                  Ship to: {addr.label || "Primary"}
                                </p>
                                <p className="text-sm text-muted-foreground">{addr.addressLine1}</p>
                                {addr.addressLine2 && (
                                  <p className="text-sm text-muted-foreground">{addr.addressLine2}</p>
                                )}
                                <p className="text-sm text-muted-foreground">
                                  {addr.city}, {addr.state} {addr.zipCode}
                                </p>
                              </div>
                            </div>
                            <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    /* Expanded address selection */
                    (<div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Shipping Address</span>
                        {addressesExpanded && selectedAddressId && selectedAddressId !== "new" && (
                          <button 
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => setAddressesExpanded(false)}
                          >
                            Collapse
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {savedAddresses.map((address) => (
                          <div
                            key={address.id}
                            className={`p-2.5 rounded-lg cursor-pointer transition-all ${
                              selectedAddressId === address.id 
                                ? 'border-2 border-primary bg-primary/5' 
                                : 'border border-border hover:border-primary/50 bg-secondary/20'
                            }`}
                            onClick={() => {
                              handleAddressChange(address.id);
                              setAddressesExpanded(false);
                            }}
                            data-testid={`address-option-${address.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                <MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="text-sm font-medium truncate">{address.label || "Primary Address"}</span>
                              </div>
                              {selectedAddressId === address.id && (
                                <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 ml-5 truncate">
                              {address.addressLine1}, {address.city}, {address.state} {address.zipCode}
                            </p>
                          </div>
                        ))}
                        <div
                          className={`p-2.5 rounded-lg cursor-pointer transition-all ${
                            selectedAddressId === "new" 
                              ? 'border-2 border-primary bg-primary/5' 
                              : 'border border-border hover:border-primary/50 bg-secondary/20'
                          }`}
                          onClick={() => {
                            handleAddressChange("new");
                            setAddressesExpanded(false);
                          }}
                          data-testid="option-new-address"
                        >
                          <div className="flex items-center gap-2">
                            <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-sm">Enter new address</span>
                          </div>
                        </div>
                      </div>
                    </div>)
                  )}
                </div>
              )}

              {/* Carrier Selection Section - only visible once minimum order is met */}
              {minimumOrderMet && (() => {
                const shipmentData = getWarehouseShipments();
                // Always show for all shipments — "Book My Own Freight" is always an option
                const shipmentsWithOptions = shipmentData;
                if (shipmentsWithOptions.length === 0) return null;

                return (
                  <div className="mt-4 space-y-3">
                    {shipmentsWithOptions.map((shipment) => {
                      const selectedCarrierIndex = selectedCarriers.get(shipment.warehouseCode) ?? 0;
                      const currentMethod = shippingMethods.get(shipment.warehouseCode) || 'FREIGHT';
                      const hasFreightQuotes = shipment.freightQuotes && shipment.freightQuotes.length > 0;
                      const hasLocalPickup = shipment.localPickupInfo?.available;

                      // Count total options: freight carriers + local pickup (if available) + Book My Own (always)
                      const totalOptions = (hasFreightQuotes ? shipment.freightQuotes!.length : 0) + (hasLocalPickup ? 1 : 0) + 1;

                      return (
                        <div key={shipment.warehouseCode} className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Truck className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium">
                              {shipmentsWithOptions.length > 1 ? `${shipment.warehouseCode} (${shipment.city}, ${shipment.region}) - ` : ''}
                              Select Shipping ({totalOptions} option{totalOptions > 1 ? 's' : ''})
                            </span>
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            {/* Local Pickup Option */}
                            {hasLocalPickup && (
                              <div
                                onClick={() => {
                                  setShippingMethods(prev => {
                                    const updated = new Map(prev);
                                    updated.set(shipment.warehouseCode, 'LOCAL_PICKUP');
                                    return updated;
                                  });
                                }}
                                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  currentMethod === 'LOCAL_PICKUP' 
                                    ? 'border-primary bg-primary/5 shadow-sm' 
                                    : 'border-border hover:border-primary/50 bg-secondary/20'
                                }`}
                                data-testid={`pickup-card-${shipment.warehouseCode}`}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <p className="font-semibold text-sm">Local Pickup</p>
                                    </div>
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-lg font-bold text-foreground">
                                      {shipment.localPickupInfo!.fee > 0 
                                        ? formatMoney(shipment.localPickupInfo!.fee) 
                                        : 'Free'}
                                    </p>
                                  </div>
                                </div>

                                {currentMethod === 'LOCAL_PICKUP' && (
                                  <div className="mt-2 pt-2 border-t border-primary/20">
                                    <div className="flex items-center gap-2 text-xs text-primary">
                                      <CheckCircle2 className="h-3 w-3" />
                                      <span className="font-medium">Selected</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Book My Own Freight Option - always available */}
                            <div
                              onClick={() => {
                                setShippingMethods(prev => {
                                  const updated = new Map(prev);
                                  updated.set(shipment.warehouseCode, 'CUSTOMER_FREIGHT');
                                  return updated;
                                });
                              }}
                              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                currentMethod === 'CUSTOMER_FREIGHT' 
                                  ? 'border-primary bg-primary/5 shadow-sm' 
                                  : 'border-border hover:border-primary/50 bg-secondary/20'
                              }`}
                              data-testid={`customer-freight-card-${shipment.warehouseCode}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="font-semibold text-sm">Book My Own Freight</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    You'll arrange pickup details after placing order
                                  </p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className="text-lg font-bold text-green-600">$0.00</p>
                                </div>
                              </div>

                              {currentMethod === 'CUSTOMER_FREIGHT' && (
                                <div className="mt-2 pt-2 border-t border-primary/20">
                                  <div className="flex items-center gap-2 text-xs text-primary">
                                    <CheckCircle2 className="h-3 w-3" />
                                    <span className="font-medium">Selected</span>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Freight loading indicator */}
                            {freightQuotesLoading && (
                              <div className="flex items-center gap-2 p-3 rounded-lg border border-border bg-secondary/10 text-sm text-muted-foreground">
                                <Truck className="h-4 w-4 animate-pulse flex-shrink-0" />
                                <span>Calculating carrier rates...</span>
                              </div>
                            )}

                            {/* Freight Carrier Options */}
                            {hasFreightQuotes && shipment.freightQuotes!.map((quote, index) => {
                              const isSelected = currentMethod === 'FREIGHT' && selectedCarrierIndex === index;
                              const handleSelectCarrier = () => {
                                setShippingMethods(prev => {
                                  const updated = new Map(prev);
                                  updated.set(shipment.warehouseCode, 'FREIGHT');
                                  return updated;
                                });
                                setSelectedCarriers(prev => {
                                  const updated = new Map(prev);
                                  updated.set(shipment.warehouseCode, index);
                                  return updated;
                                });
                              };

                              return (
                                <div
                                  key={index}
                                  onClick={handleSelectCarrier}
                                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                    isSelected 
                                      ? 'border-primary bg-primary/5 shadow-sm' 
                                      : 'border-border hover:border-primary/50 bg-secondary/20'
                                  }`}
                                  data-testid={`carrier-card-${shipment.warehouseCode}-${index}`}
                                >
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <p className="font-semibold text-sm">{quote.carrier}</p>
                                        {quote.serviceLevel && (
                                          <Badge variant="outline" className="text-xs">{quote.serviceLevel}</Badge>
                                        )}
                                      </div>
                                      {quote.carrierType && (
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                          {quote.carrierType === 'UBER_FREIGHT' ? 'Uber Freight' : 'Standard Freight'}
                                        </p>
                                      )}
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      <p className="text-lg font-bold text-foreground">{formatMoney(quote.totalCost)}</p>
                                      {quote.transitEstimate?.durationDays && (
                                        <p className="text-xs text-muted-foreground">{quote.transitEstimate.durationDays} days</p>
                                      )}
                                    </div>
                                  </div>

                                  <div className="space-y-1 text-xs text-muted-foreground">
                                    <div className="flex justify-between">
                                      <span>Base Freight{quote.chargeableWeight ? ` (${quote.chargeableWeight} lbs)` : ''}</span>
                                      <span>{formatMoney(quote.baseFreight)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Fuel Surcharge{quote.fuelSurchargePercent ? ` (${quote.fuelSurchargePercent}%)` : ''}</span>
                                      <span>{formatMoney(quote.fuelSurcharge)}</span>
                                    </div>
                                    {quote.distanceSurcharge !== undefined && quote.distanceSurcharge > 0 && (
                                      <div className="flex justify-between">
                                        <span>Distance Surcharge</span>
                                        <span>{formatMoney(quote.distanceSurcharge)}</span>
                                      </div>
                                    )}
                                    {quote.accessorialCharges && quote.accessorialCharges.length > 0 && (
                                      <>
                                        {quote.accessorialCharges.map((charge) => (
                                          <div key={charge.code} className="flex justify-between">
                                            <span>{charge.name}</span>
                                            <span>{formatMoney(charge.amount)}</span>
                                          </div>
                                        ))}
                                      </>
                                    )}
                                  </div>

                                  {isSelected && (
                                    <div className="mt-2 pt-2 border-t border-primary/20">
                                      <div className="flex items-center gap-2 text-xs text-primary">
                                        <CheckCircle2 className="h-3 w-3" />
                                        <span className="font-medium">Selected</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Cart Conflicts Modal */}
      <CartConflictsModal
        conflicts={cartConflicts}
        onClose={() => setCartConflicts(null)}
        onAcknowledge={handleConflictsAcknowledged}
      />
      {/* Admin-only: Freight payload inspector modal */}
      {isAdminUser && (
        <Dialog open={freightDebugOpen} onOpenChange={setFreightDebugOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="modal-freight-debug">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bug className="h-5 w-5" />
                Freight Request Payload (Admin)
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-xs">
              {freightDebug.size === 0 && (
                <p className="text-muted-foreground" data-testid="text-freight-debug-empty">
                  No freight payload captured yet. Enter a valid destination to generate quotes.
                </p>
              )}
              {Array.from(freightDebug.entries()).map(([warehouseCode, dbg]) => {
                const efw = dbg?.efw;
                const req = efw?.request;
                return (
                  <div
                    key={warehouseCode}
                    className="border rounded-md p-3 space-y-3"
                    data-testid={`section-freight-debug-${warehouseCode}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm">Shipment: {warehouseCode}</span>
                      <span className="text-muted-foreground">
                        EFW {dbg?.efwActive ? 'active' : 'inactive'}
                      </span>
                    </div>

                    {dbg?.error && (
                      <div className="rounded bg-destructive/10 border border-destructive/20 p-2 text-destructive">
                        Error: {dbg.error}
                      </div>
                    )}

                    {req ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <p className="font-medium">Origin</p>
                            <p className="text-muted-foreground">
                              {req.originCity}, {req.originState} {req.originZip}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium">Destination</p>
                            <p className="text-muted-foreground">
                              {req.destinationCity}, {req.destinationState} {req.destinationZip}
                            </p>
                          </div>
                        </div>

                        <div>
                          <p className="font-medium">Accessorials</p>
                          <p className="text-muted-foreground">
                            {req.accessorials && req.accessorials.length > 0
                              ? req.accessorials
                                  .map((a: any) => (typeof a === 'string' ? a : a?.code))
                                  .filter(Boolean)
                                  .join(', ')
                              : 'None'}
                          </p>
                        </div>

                        <div>
                          <p className="font-medium mb-1">Commodities</p>
                          <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                              <thead>
                                <tr className="text-muted-foreground border-b">
                                  <th className="py-1 pr-2 font-medium">Description</th>
                                  <th className="py-1 px-2 font-medium">Pieces</th>
                                  <th className="py-1 px-2 font-medium">Wt/pc (lbs)</th>
                                  <th className="py-1 px-2 font-medium">L×W×H (in)</th>
                                  <th className="py-1 px-2 font-medium">Class</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(req.commodities ?? []).map((c: any, i: number) => (
                                  <tr key={i} className="border-b last:border-0" data-testid={`row-commodity-${warehouseCode}-${i}`}>
                                    <td className="py-1 pr-2">{c.description}</td>
                                    <td className="py-1 px-2">{c.numberOfPieces}</td>
                                    <td className="py-1 px-2">{c.weightPerPiece}</td>
                                    <td className="py-1 px-2">{c.length}×{c.width}×{c.height}</td>
                                    <td className="py-1 px-2">{c.class}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <details className="text-muted-foreground">
                          <summary className="cursor-pointer font-medium text-foreground">
                            Raw request
                          </summary>
                          <pre className="mt-1 whitespace-pre-wrap break-all bg-muted/50 rounded p-2">
                            {JSON.stringify(req, null, 2)}
                          </pre>
                        </details>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">
                        No EFW request was built for this shipment (EFW not configured).
                      </p>
                    )}

                    <details className="text-muted-foreground">
                      <summary className="cursor-pointer font-medium text-foreground">
                        Carrier response{efw?.responseStatus ? ` (HTTP ${efw.responseStatus})` : ''}
                      </summary>
                      <pre className="mt-1 whitespace-pre-wrap break-all bg-muted/50 rounded p-2">
                        {efw?.responseBody
                          ? (typeof efw.responseBody === 'string'
                              ? efw.responseBody
                              : JSON.stringify(efw.responseBody, null, 2))
                          : 'No response captured.'}
                      </pre>
                    </details>
                  </div>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Video Modal */}
      <Dialog open={!!selectedVideoItem} onOpenChange={(open) => !open && setSelectedVideoItem(null)}>
        <DialogContent className="max-w-4xl" data-testid="video-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              Grade {selectedVideoItem?.grade} - Serial: {selectedVideoItem?.serialNumber}
            </DialogTitle>
          </DialogHeader>
          {selectedVideoItem?.videoUrl && (
            <VideoPlayer
              src={selectedVideoItem.videoUrl}
              className="w-full aspect-video"
              autoPlay={true}
            />
          )}
        </DialogContent>
      </Dialog>
      {/* Local Pickup Confirmation Modal */}
      <Dialog open={showPickupConfirmModal} onOpenChange={(open) => {
        if (!open) {
          setShowPickupConfirmModal(false);
          setPickupConfirmText("");
          setPendingFormData(null);
        }
      }}>
        <DialogContent className="max-w-md" data-testid="pickup-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Local Pickup Terms
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground space-y-3">
              <p>
                By selecting local pickup, you agree to the following terms:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>
                  <span className="font-semibold text-foreground">Payment:</span> Wire transfer must be received by <span className="font-bold text-foreground">{formatTermsDate(wireDeadline)}</span>.
                </li>
                <li>
                  <span className="font-semibold text-foreground">Pickup:</span> Items must be picked up by <span className="font-bold text-foreground">{formatTermsDate(readinessDeadline)}</span>{latestPresaleDate ? ` (5 business days after your last arriving item on ${latestPresaleDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})` : ''}.
                </li>
              </ul>
              <p className="text-amber-600 dark:text-amber-500 font-medium">
                Failure to meet these terms may result in order cancellation.
              </p>
            </div>
            <div className="space-y-2">
              <label htmlFor="pickup-confirm-input" className="text-sm font-medium">
                Type "I agree" to confirm:
              </label>
              <Input
                id="pickup-confirm-input"
                value={pickupConfirmText}
                onChange={(e) => setPickupConfirmText(e.target.value)}
                placeholder="I agree"
                className="w-full"
                data-testid="input-pickup-confirm"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPickupConfirmModal(false);
                  setPickupConfirmText("");
                  setPendingFormData(null);
                }}
                className="flex-1"
                data-testid="button-pickup-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePickupConfirm}
                disabled={pickupConfirmText.toLowerCase() !== "i agree" || createOrderMutation.isPending}
                className="flex-1"
                data-testid="button-pickup-confirm"
              >
                {createOrderMutation.isPending ? "Processing..." : "Place Order"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Our Freight (Esty's) Terms Confirmation Modal */}
      <Dialog open={showFreightTermsModal} onOpenChange={(open) => { if (!open) { setShowFreightTermsModal(false); setFreightConfirmText(""); setPendingFormData(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🚛</span>
              Freight Shipping Terms
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground space-y-3">
              <p>
                We will arrange freight pickup through our carrier (Estes Forwarding Worldwide). By placing this order, you agree to the following terms:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>
                  <span className="font-semibold text-foreground">Payment:</span> Wire transfer must be received by <span className="font-bold text-foreground">{formatTermsDate(wireDeadline)}</span>.
                </li>
                <li>
                  <span className="font-semibold text-foreground">Freight:</span> We will schedule pickup once payment is confirmed. Freight is arranged on your behalf at the quoted rate.
                </li>
                {latestPresaleDate && (
                  <li>
                    <span className="font-semibold text-foreground">Pre-arrival items:</span> Your order includes items expected to arrive around <span className="font-bold text-foreground">{latestPresaleDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>. Freight will be scheduled after all items are ready.
                  </li>
                )}
              </ul>
              <p className="text-amber-600 dark:text-amber-500 font-medium">
                Failure to wire by the payment deadline will result in freight cancellation and order cancellation.
              </p>
            </div>
            <div className="space-y-2">
              <label htmlFor="freight-confirm-input" className="text-sm font-medium">
                Type "I agree" to confirm:
              </label>
              <Input
                id="freight-confirm-input"
                data-testid="input-freight-confirm"
                placeholder="I agree"
                value={freightConfirmText}
                onChange={(e) => setFreightConfirmText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleFreightConfirm(); }}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-2">
            <Button
              variant="outline"
              data-testid="button-freight-cancel"
              onClick={() => { setShowFreightTermsModal(false); setFreightConfirmText(""); setPendingFormData(null); }}
            >
              Cancel
            </Button>
            <Button
              data-testid="button-freight-confirm"
              onClick={handleFreightConfirm}
              disabled={createOrderMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {createOrderMutation.isPending ? "Processing..." : "Place Order"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Freight (Book Your Own) Terms Confirmation Modal */}
      <Dialog open={showCustomerFreightTermsModal} onOpenChange={(open) => { if (!open) { setShowCustomerFreightTermsModal(false); setCustomerFreightConfirmText(""); setPendingFormData(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">📦</span>
              Customer-Arranged Freight Terms
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground space-y-3">
              <p>
                You have chosen to arrange your own freight carrier. By placing this order, you agree to the following terms:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>
                  <span className="font-semibold text-foreground">Payment:</span> Wire transfer must be received by <span className="font-bold text-foreground">{formatTermsDate(wireDeadline)}</span>.
                </li>
                <li>
                  <span className="font-semibold text-foreground">Your responsibility:</span> You are solely responsible for scheduling freight pickup, all carrier costs, billing disputes, and coordination. OpenBox by ShopJimmy has no involvement in your freight arrangement.
                </li>
                <li>
                  <span className="font-semibold text-foreground">No liability:</span> OpenBox by ShopJimmy will not be responsible for any shipping expenses, delays, or carrier disputes.
                </li>
                {latestPresaleDate && (
                  <li>
                    <span className="font-semibold text-foreground">Pre-arrival items:</span> Items are expected to arrive around <span className="font-bold text-foreground">{latestPresaleDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>. Please coordinate your carrier pickup accordingly.
                  </li>
                )}
              </ul>
              <p className="text-amber-600 dark:text-amber-500 font-medium">
                By confirming, you acknowledge that all freight costs and scheduling are entirely your responsibility.
              </p>
            </div>
            <div className="space-y-2">
              <label htmlFor="customer-freight-confirm-input" className="text-sm font-medium">
                Type "I agree" to confirm:
              </label>
              <Input
                id="customer-freight-confirm-input"
                data-testid="input-customer-freight-confirm"
                placeholder="I agree"
                value={customerFreightConfirmText}
                onChange={(e) => setCustomerFreightConfirmText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCustomerFreightConfirm(); }}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-2">
            <Button
              variant="outline"
              data-testid="button-customer-freight-cancel"
              onClick={() => { setShowCustomerFreightTermsModal(false); setCustomerFreightConfirmText(""); setPendingFormData(null); }}
            >
              Cancel
            </Button>
            <Button
              data-testid="button-customer-freight-confirm"
              onClick={handleCustomerFreightConfirm}
              disabled={createOrderMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {createOrderMutation.isPending ? "Processing..." : "Place Order"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}